import { useEffect, useMemo, useRef, useState } from 'react';
import { AudioBus, reconcileLedgerSubtotal, Easings, TweenGroup } from '@goodtime/engine';
import type { Theme } from '@goodtime/engine';
import { BetterLuckDialog } from './BetterLuckDialog';
import {
  ITEMS,
  WHEEL_PHASE_META,
  type BonusWedgePlacement,
  type BotArchetype,
  type ItemId,
  type WheelActionCard,
  type WheelDial,
  type WheelDialOutcome,
  type WheelObservableState,
  type WheelSeatView,
  type WheelWedge,
} from '@goodtime/shared';
import { Icon } from '../Icon';
import { SeatAvatar, useSeatName } from './SeatAvatar';
import { useWheelDims, type WheelDims } from './wheelDims';
import { wheelBiomeBackdropUrl } from './wheelBiomeBackdrop';
import { submitWheelCommand } from '../../api/wheelCommand';
import { WheelResolveCanvas } from './WheelResolveCanvas';
import wheelHubUrl from '../../assets/wheel/hub.png';
import wheelOuterRimUrl from '../../assets/wheel/outer_rim.png';

/** Item-icon URL lookup, keyed by {@link ItemId}. Vite's
 *  {@code import.meta.glob} discovers every PNG in
 *  {@code apps/mobile/src/assets/item/} at build time and exposes
 *  the URLs synchronously (eager). Used by {@link ItemDeployStrip}
 *  so the tap-to-deploy tiles show the commissioned art for each
 *  item rather than placeholder text labels. */
const ITEM_ICON_MODULES = import.meta.glob<{ default: string }>(
  '../../assets/item/*.png',
  { eager: true },
);
const ITEM_ICON_URL_BY_ID: Partial<Record<ItemId, string>> = Object.fromEntries(
  Object.entries(ITEM_ICON_MODULES)
    .map(([path, mod]) => {
      const filename = path.split('/').pop()?.replace(/\.png$/, '');
      if (!filename) return null;
      return [filename.toUpperCase() as ItemId, mod.default] as const;
    })
    .filter((entry): entry is readonly [ItemId, string] => entry !== null),
);
export const WHEEL_ITEM_ICON_URLS = ITEM_ICON_URL_BY_ID;

export interface WheelResolveViewProps {
  state: WheelObservableState;
  selfSeatId?: number | null;
  /** Active room theme — drives the painted backdrop ring. Null
   *  falls back to no painted backdrop (just the page gradient). */
  theme?: Theme | null;
  /** Player + room ids — only used during the STAKE pre-spin window
   *  to submit SELECT_SEAT / DEPLOY_ITEM commands. Optional so the
   *  dev preview can mount the view without a backend wired. */
  playerId?: string | null;
  roomId?: string | null;
  /** U11 (2026-05-30) — phase-gated rim-avatar tap. STAKE keeps
   *  SELECT_SEAT (swap); WHEEL_REVEAL / WHEEL_RESOLVE invoke this
   *  callback with the archetype at the tapped slot so the parent
   *  ({@link WheelLobbyView}) can open a {@code ProfileCard}. Null
   *  disables the affordance during the spin. */
  onArchetypeTap?: ((archetype: BotArchetype) => void) | null;
}

const STAKE_WINDOW_MS = 10_000;

// Wheel sizing comes from {@link useWheelDims} so the disc, seat
// avatars and label radii all scale together across mobile / tablet /
// desktop. The wedge count + arc are invariants.
const SPIN_DURATION_MS = 7000;
/** Per-card display time in the post-spin action-card sequence.
 *  Bumped from 1000ms → 1500ms (producer brief 2026-05-13: "still
 *  a bit fast to see what's happening — add 500ms"). At 1 deploy
 *  per seat × 6 seats = 9s max sequence; the 12s WHEEL_RESOLVE
 *  phase still wraps it without overrunning. */
/** How long each action card holds before the next one plays.
 *  Producer brief 2026-05-14: "it was very quick and didn't show
 *  what the effect was to each player" — bumped from 1500 → 2500
 *  so the headline + coin-impact delta have time to read. The
 *  OutcomeChip itemised ledger comes after this sequence and gives
 *  the per-seat breakdown. */
const ACTION_CARD_DURATION_MS = 2500;
const SPIN_REVOLUTIONS = 6;
const CHIP_REVEAL_STAGGER_MS = 220;
const WEDGE_COUNT = 24;
const WEDGE_ARC_DEG = 360 / WEDGE_COUNT;
const SEAT_POINTER_OFFSETS_DEG = [0, 60, 120, 180, 240, 300] as const;

/**
 * WHEEL_RESOLVE phase — spatial dial-wheel spin reveal.
 *
 * <p>The wheel is 24 wedges (15° each). Six seat-pointers orbit the
 * rim at fixed 60° intervals (seat 1 = 12 o'clock, seat 2 = 2 o'clock,
 * etc.). The wheel spins for {@code SPIN_DURATION_MS}, lands at the
 * server-computed {@code landingAngleDeg}, and each seat-pointer
 * reads its own wedge — per-seat outcome chips fan out in
 * payout-descending order.
 *
 * <p>Per-seat math: {@code payout = stake × multiplier} for COINS
 * wedges, zero for BANKRUPT / SKIP_NEXT / EXTRA_ITEM / SPECIAL_PRIZE
 * (effect resolution lands in Phase 5).
 */
export function WheelResolveView({
  state,
  selfSeatId = null,
  theme = null,
  playerId = null,
  roomId = null,
  onArchetypeTap = null,
}: WheelResolveViewProps) {
  const phaseMeta = WHEEL_PHASE_META.WHEEL_RESOLVE;
  const resolve = state.dialResolveResult;
  // Net winnings for the local seat — the same WINNINGS the central
  // ledger shows. When the server sends a structured ledger, reconcile
  // the subtotal (never negative) and add its line deltas; otherwise the
  // spin payout already bakes every effect, so the take is the floored
  // payout (the widget's fallback collapses to the same value). Drives
  // the zero-winnings "better luck" dialogue below.
  const selfWinnings = useMemo<number | null>(() => {
    if (!resolve || selfSeatId == null) return null;
    const payout = resolve.perSeatOutcomes[String(selfSeatId)]?.payout ?? 0;
    const serverLines = resolve.ledgerLinesBySeat?.[String(selfSeatId)];
    if (serverLines) {
      return (
        reconcileLedgerSubtotal(payout, serverLines) +
        serverLines.reduce((sum, l) => sum + l.delta, 0)
      );
    }
    return Math.max(0, payout);
  }, [resolve, selfSeatId]);
  const dial = resolve?.dial ?? state.currentDial ?? null;
  const biomeBackdropUrl = wheelBiomeBackdropUrl(theme);
  // Touch useWheelDims so the legacy {@code WheelStage} (still
  // present for visual-parity audit) keeps a live reference. The
  // hook is removed once that audit completes.
  void useWheelDims;
  const isPreSpin = state.phase === 'STAKE';

  // Producer feedback 2026-05-27 — "hide the Round N card once the
  // wheel completes." During STAKE the banner reads
  // "ROUND N · PICK YOUR SEAT" (keep). During the spin it reads
  // "ROUND N" (keep — context for the spin). Once the wheel lands
  // (after SPIN_DURATION_MS), drop it so the seat-fx + outcome
  // chips own the screen.
  const [bannerVisible, setBannerVisible] = useState(true);
  const matchNumber = state.matchNumber ?? null;
  useEffect(() => {
    if (state.phase === 'STAKE') {
      setBannerVisible(true);
      return;
    }
    if (state.phase !== 'WHEEL_RESOLVE') return;
    // Re-show on every fresh WHEEL_RESOLVE entry (new match), then
    // hide once the spin completes.
    setBannerVisible(true);
    const t = window.setTimeout(() => setBannerVisible(false), SPIN_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [state.phase, matchNumber]);

  // Zero-winnings consolation — when the local seat's net winnings land
  // at zero (or below), show the "better luck" dialogue once the spin
  // has visibly landed (SPIN_DURATION_MS) plus a beat for the ledger
  // reveal. Resets whenever the phase or match changes.
  const [showBetterLuck, setShowBetterLuck] = useState(false);
  useEffect(() => {
    if (
      state.phase !== 'WHEEL_RESOLVE' ||
      selfWinnings == null ||
      selfWinnings > 0
    ) {
      setShowBetterLuck(false);
      return;
    }
    const t = window.setTimeout(
      () => setShowBetterLuck(true),
      SPIN_DURATION_MS + 1200,
    );
    return () => window.clearTimeout(t);
  }, [state.phase, matchNumber, selfWinnings]);

  // Producer brief 2026-05-16 — the legacy SpinPlaceholder branch
  // here unmounted the WheelResolveCanvas during the brief STAKE →
  // WHEEL_RESOLVE transition tick (when phase had flipped but
  // dialResolveResult hadn't yet), forcing Pixi to re-init and
  // leaving the wheel blank for ~500ms each round. The Pixi scene
  // tolerates null dial / resolve safely (disc just shows no
  // wedges; spin tween waits for the next setState push), so the
  // placeholder is no longer needed.
  void dial;
  void resolve;
  void isPreSpin;

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        background:
          'radial-gradient(ellipse at center, #2A0712 0%, #0A0518 60%, #060d22 100%)',
        color: '#E9F0FF',
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      {/* Biome-panorama backdrop — matches the Reel Race surface so
          the room feels continuous as it shifts from race to spin. */}
      {biomeBackdropUrl ? (
        <img
          src={biomeBackdropUrl}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.55,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at center, transparent 0%, transparent 45%, rgba(0,0,0,0.55) 100%)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      {bannerVisible ? (
        <ResolveBanner
          label={
            isPreSpin
              ? `ROUND ${state.matchNumber ?? '?'} · PICK YOUR SEAT`
              : `ROUND ${state.matchNumber ?? '?'}`
          }
          accent={phaseMeta.accent}
        />
      ) : null}
      {/* Producer brief 2026-05-16 — the wheel surface (disc, hub,
       *  rim, perimeter avatars, outcome chips, action-card overlay,
       *  per-avatar SeatItemFx, STAKE clock-fill ring, confetti
       *  burst, BONUS_WEDGE markers, BOOST telegraph) is now
       *  Pixi-rendered via {@link WheelResolveCanvas}. The legacy
       *  {@code WheelStage} React tree was retired in this turn.
       *  Only the item-deploy strip (input-heavy UX) stays React. */}
      <WheelResolveCanvas
        state={state}
        selfSeatId={selfSeatId}
        playerId={playerId ?? null}
        roomId={roomId ?? null}
        onArchetypeTap={onArchetypeTap}
      />
      {/* Zero-winnings consolation — covers the Pixi WINNINGS ledger
          with a "better luck next time" card when the spin nets zero. */}
      {showBetterLuck ? <BetterLuckDialog /> : null}
      {/* Item-deploy strip — visible during STAKE for the local
       *  player, sits below the wheel + countdown. Each owned item
       *  becomes a tap-to-deploy tile that fires DEPLOY_ITEM. Once
       *  armed the tile greys out; the wheel resolver consumes the
       *  deployed items when it spins. */}
      {/* ItemDeployStrip retired 2026-05-16 — the deploy tiles now
       *  live inside {@link WheelResolveCanvas} (Pixi) so the whole
       *  wheel surface is a single render tree per producer brief. */}

      {/* Reaction tray moved to the room level (WheelLobbyView) so it
          sits in one consistent bottom-right spot across every phase.
          Per-seat ReactionFloat overlays for the wheel-rim avatars
          still want to live inside WheelResolveCanvas (Pixi) — that
          remains a follow-up. */}
    </div>
  );
}

// ---- placeholder ---------------------------------------------------------

// SpinPlaceholder retained as dead code for the visual-parity
// audit. Will be deleted en bloc with the legacy WheelStage once
// the canvas port is signed off.
// @ts-expect-error — retained for visual-parity audit.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function SpinPlaceholder() {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(ellipse at center, #2A0712 0%, #0A0518 60%, #060d22 100%)',
        color: '#FFE48A',
        fontFamily: '"Fredoka", -apple-system, system-ui',
        fontSize: 14,
        fontWeight: 700,
        letterSpacing: 1,
      }}
    >
      Spinning the wheel…
    </div>
  );
}

// ---- subcomponents --------------------------------------------------------

function ResolveBanner({ label, accent }: { label: string; accent: string }) {
  // Banner anchored BELOW the BalancePill, which sits at
  // calc(safe-area-inset-top + 4px) and is ~44px tall. We add the SAME
  // safe-area inset here so the banner clears the pill on notched phones
  // (the old fixed top:70 ignored the inset, so the iOS pill dropped onto
  // the banner, producer 2026-06-10). The pill carries the bonus pot +
  // items on its left side (2026-05-11 BonusHud fold-in).
  return (
    <div
      style={{
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top, 0px) + 70px)',
        left: '50%',
        transform: 'translateX(-50%)',
        // Producer brief 2026-05-12: "make the text smaller to fit
        // the round N sign". Tighter padding + 12px font keeps the
        // PICK YOUR SEAT version on one line on a 375px viewport.
        padding: '5px 14px',
        borderRadius: 9,
        background: 'linear-gradient(180deg, #4A0F1F 0%, #2A0712 100%)',
        border: `2px solid ${accent}`,
        boxShadow: `0 0 14px ${accent}55, 0 6px 14px rgba(0,0,0,0.5)`,
        fontSize: 12,
        fontWeight: 900,
        letterSpacing: 1.4,
        color: '#FFE48A',
        textShadow: '0 1px 2px rgba(0,0,0,0.6)',
        whiteSpace: 'nowrap',
        zIndex: 5,
      }}
    >
      {label}
    </div>
  );
}

/**
 * Wheel + perimeter seat-pointers + per-seat outcome chips. Owns the
 * {@link TweenGroup} that drives the spin, tick SFX cadence, and
 * staggered chip reveals.
 */
// Producer brief 2026-05-16 — WheelStage retired in favour of
// {@link WheelResolveCanvas} (Pixi). The function below stays in
// the file (un-exported) as a quick reference while we audit the
// canvas port for visual parity; once that's signed off, the dead
// helper functions it transitively references (SeatItemFxLayer,
// SeatPointer, ActionCardOverlay, etc.) will be deleted en bloc.
// Suppress the noUnusedLocals warning until that cleanup pass.
// @ts-expect-error — retained for visual-parity audit.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function WheelStage({
  dial,
  resolve,
  seats,
  selfSeatId,
  dims,
  isPreSpin,
  seatPointerSlots,
  bonusWedgePlacements,
  playerId,
  roomId,
  matchNumber,
}: {
  dial: WheelDial;
  resolve: WheelObservableState['dialResolveResult'];
  seats: ReadonlyArray<WheelSeatView>;
  selfSeatId: number | null;
  dims: WheelDims;
  /** STAKE → static disc + interactive seat-pick + items.
   *  WHEEL_RESOLVE → spin tween + outcome chips. */
  isPreSpin: boolean;
  /** seatId → pointer slot for the current round. Drives the pre-
   *  spin tap-to-swap UI as well as the resolve-time pointer placement. */
  seatPointerSlots: Record<string, number>;
  /** Producer brief 2026-05-15: BONUS_WEDGE deploys land on the dial
   *  IMMEDIATELY for all players (rather than only at WHEEL_RESOLVE).
   *  The server commits the wedge index at DEPLOY_ITEM time and
   *  broadcasts via this list; the client overlays a coin marker +
   *  deployer avatar at the wedge position. */
  bonusWedgePlacements: ReadonlyArray<BonusWedgePlacement>;
  /** Only used during STAKE to fire SELECT_SEAT / DEPLOY_ITEM. */
  playerId: string | null;
  roomId: string | null;
  /** Resets the STAKE-window timer when the match flips. */
  matchNumber: number | null;
}) {
  // Build the conic-gradient string from per-wedge accents.
  const conicSegments = useMemo(() => {
    const segs: string[] = [];
    dial.wedges.forEach((wedge, i) => {
      const start = i * WEDGE_ARC_DEG;
      const end = (i + 1) * WEDGE_ARC_DEG;
      segs.push(`${wedgeAccent(wedge)} ${start}deg ${end}deg`);
    });
    return segs.join(', ');
  }, [dial.wedges]);

  // Settle rotation: make the world-frame 0° pointer read the
  // server-computed landingWedgeIndex. Since pointer-at-0° reads
  // floor((local-angle) / 15) after the wheel rotates by r and a
  // wedge at local-angle w lands at world-angle (w + r) mod 360,
  // we need w = -r at world-angle 0 → r = -w (mod 360). The server
  // gives us landingAngleDeg = ((-r) mod 360); so r = -landingAngleDeg
  // (mod 360). After SPIN_REVOLUTIONS, we add the rest. Null during
  // STAKE — the spin tween is gated off, the disc holds at rotation
  // 0 while the player picks a seat / deploys items.
  //
  // Producer brief 2026-05-15 — when MAGNET fires this round the
  // server reports both the baseline (no-magnet) and the final
  // (magnet-pulled) landing angles. We tween the spin to the
  // BASELINE first, hold for a beat while the magnet action card
  // flashes on the deployer's avatar, then tween the remaining
  // delta so the player sees the wheel actually steal the wedge.
  const settleRotation = useMemo(
    () => (resolve ? SPIN_REVOLUTIONS * 360 - resolve.landingAngleDeg : 0),
    [resolve],
  );
  const baselineSettleRotation = useMemo(
    () =>
      resolve
        ? SPIN_REVOLUTIONS * 360 - resolve.baselineLandingAngleDeg
        : 0,
    [resolve],
  );
  /** True iff MAGNET pulled the wheel this round — server reports
   *  a baseline angle different from the final landing angle. The
   *  spin tween branches on this to play the two-stage animation. */
  const magnetPullActive = useMemo(() => {
    if (!resolve) return false;
    const baseline = resolve.baselineLandingAngleDeg;
    const final_ = resolve.landingAngleDeg;
    return Math.abs(baseline - final_) > 0.5;
  }, [resolve]);
  // (The MAGNET deployer's avatar surfaces the magnet icon during
  // the pull via {@link SeatItemFxLayer} — affectedSeatsForCard
  // already returns [sourceSeatId] for MAGNET, so no extra wiring
  // is needed here.)

  const [rotation, setRotation] = useState(0);
  const [landed, setLanded] = useState(false);
  const [revealedSeatIds, setRevealedSeatIds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  /** Index of the action card currently flashing on screen, or
   *  {@code null} when no card is active. The post-spin sequencer
   *  walks {@code resolve.actionCards} one at a time before the
   *  chip-reveal stagger fires, so every player sees the story of
   *  how the wheel resolved (Player X boosted · Player Y hit Z ·
   *  SHIELD blocked · ...). */
  const [actionCardIndex, setActionCardIndex] = useState<number | null>(null);
  /** Filtered action-card list shown in the central overlay.
   *
   *  Two filters applied:
   *  1. Per-rival LIGHTNING cards (target=rival) — kept on the
   *     wire so each OutcomeChip's ledger can show "Lightning −80"
   *     for that rival, but the overlay only plays the ONE summary
   *     card per LIGHTNING deploy.
   *  2. Single-player items (BOOST, TRIPLE_BOOST, MAGNET, X2_BONUS,
   *     COIN_STASH) only play their action card on the deployer's
   *     own screen. Other players' clients suppress these because
   *     the actions don't change anyone else's pot — producer
   *     brief 2026-05-14: "any single player actions are filtered
   *     to the current player". OutcomeChip line-items already
   *     respect this private-to-source rule via {@link
   *     buildOutcomeLineItems}.
   *
   *  {@link actionCardIndex} indexes into this list, not the raw
   *  {@code resolve.actionCards}. */
  const displayActionCards = useMemo<ReadonlyArray<WheelActionCard>>(() => {
    const cards = resolve?.actionCards ?? [];
    return cards.filter((c) => {
      // Per-rival LIGHTNING / POW cards exist on the wire so each
      // rival's coin-loss floater + OutcomeChip ledger can resolve
      // their share; the central overlay only plays the SUMMARY
      // card per deploy.
      if (c.itemId === 'LIGHTNING' && c.targetSeatId !== null) return false;
      if (c.itemId === 'POW' && c.targetSeatId !== null) return false;
      if (
        c.itemId === 'BOOST' ||
        c.itemId === 'TRIPLE_BOOST' ||
        c.itemId === 'MAGNET' ||
        c.itemId === 'X2_BONUS' ||
        c.itemId === 'COIN_STASH'
      ) {
        // Single-player items — only the deployer sees the card.
        return selfSeatId !== null && c.sourceSeatId === selfSeatId;
      }
      return true;
    });
  }, [resolve?.actionCards, selfSeatId]);

  // STAKE-window countdown — drives the clock-fill ring and the
  // dialog timer number. The wheel disc stays stationary while this
  // runs; the spin tween below kicks in when the phase flips.
  const stakeStartRef = useRef<number>(Date.now());
  const [stakeNow, setStakeNow] = useState<number>(Date.now());
  useEffect(() => {
    if (!isPreSpin) return;
    stakeStartRef.current = Date.now();
    setStakeNow(Date.now());
    const id = window.setInterval(() => setStakeNow(Date.now()), 50);
    return () => window.clearInterval(id);
  }, [isPreSpin, matchNumber]);
  const stakeElapsed = stakeNow - stakeStartRef.current;
  const stakeProgress = Math.min(1, Math.max(0, stakeElapsed / STAKE_WINDOW_MS));
  const stakeRemainingSec = Math.max(
    0,
    Math.ceil((STAKE_WINDOW_MS - stakeElapsed) / 1000),
  );

  useEffect(() => {
    // Spin tween only fires once we have a resolve payload — i.e.
    // we're in WHEEL_RESOLVE. During STAKE the disc holds still.
    if (!resolve || isPreSpin) {
      setRotation(0);
      setLanded(false);
      setRevealedSeatIds(new Set());
      return;
    }
    const group = new TweenGroup();

    let lastTickRotation = 0;
    const TICK_PER_DEG = 18;

    // Producer brief 2026-05-15 — main spin tween targets the
    // BASELINE landing (the no-magnet result). When MAGNET fired,
    // a second "pull" tween rotates the wheel from the baseline
    // onto the magnet-final position, so the player sees the wheel
    // settle naturally, pause, then get yanked. When no magnet
    // fired, baselineSettleRotation === settleRotation and the
    // second tween is a no-op skip.
    group.start({
      durationMs: SPIN_DURATION_MS,
      easing: Easings.easeOutQuart,
      onUpdate: (p) => {
        const r = p * baselineSettleRotation;
        setRotation(r);
        if (Math.floor(r / TICK_PER_DEG) > Math.floor(lastTickRotation / TICK_PER_DEG)) {
          AudioBus.shared.play('wheel:tick', { volume: 0.4 });
          lastTickRotation = r;
        }
      },
      onComplete: () => {
        AudioBus.shared.play('wheel:land', { volume: 0.8 });

        // Self-seat BOOST nudge. Server lands the wheel at its
        // ORIGINAL angle (boosts shift the per-seat READING, not the
        // wheel itself). To make the +1-peg shift visible, after the
        // action-card sequence finishes we nudge the wheel by 15°
        // (one wedge) so the boosted wedge ends up under the
        // player's pointer.
        const selfOutcome =
          selfSeatId !== null
            ? resolve.perSeatOutcomes[String(selfSeatId)] ?? null
            : null;
        // BOOST or TRIPLE_BOOST both produce a wedge shift on the
        // deployer's own pointer; either triggers the nudge anim.
        // The actual shift magnitude (1 vs 3) is read from the
        // base→final delta, so the animation scales naturally.
        const boostShift =
          selfOutcome &&
          (selfOutcome.appliedItems.includes('BOOST') ||
            selfOutcome.appliedItems.includes('TRIPLE_BOOST')) &&
          selfOutcome.baseWedgeIndex !== selfOutcome.wedgeIndex
            ? (() => {
                const raw =
                  (selfOutcome.wedgeIndex - selfOutcome.baseWedgeIndex + 24) % 24;
                return raw > 12 ? raw - 24 : raw;
              })()
            : 0;
        const nudgeDeg = -boostShift * WEDGE_ARC_DEG;

        const fireChipReveal = () => {
          setLanded(true);
          // Reveal per-seat outcome chips in payout-descending order
          // so the biggest wins land first — drama. Non-COINS wedges
          // are grouped at the end (Phase 5 will wire their resolution).
          const outcomeEntries = Object.entries(resolve.perSeatOutcomes)
            .map(([seatIdStr, outcome]) => ({
              seatId: Number(seatIdStr),
              outcome,
            }))
            .sort((a, b) => b.outcome.payout - a.outcome.payout);

          outcomeEntries.forEach(({ seatId }, idx) => {
            const fireAt = idx * CHIP_REVEAL_STAGGER_MS;
            window.setTimeout(() => {
              setRevealedSeatIds((prev) => {
                if (prev.has(seatId)) return prev;
                const next = new Set(prev);
                next.add(seatId);
                return next;
              });
              AudioBus.shared.play('chip:drop', { volume: 0.5 });
            }, fireAt);
          });

          const ceremonyAt =
            outcomeEntries.length * CHIP_REVEAL_STAGGER_MS + 400;
          window.setTimeout(() => {
            AudioBus.shared.play('wheel:ceremony', { volume: 0.7 });
          }, ceremonyAt);
        };

        const fireBoostNudge = () => {
          if (nudgeDeg === 0) {
            fireChipReveal();
            return;
          }
          // 400ms tween that adds nudgeDeg to the rotation. Ratchet
          // a soft 'tick' at the halfway point to sell the "click
          // into place" feel.
          const nudgeStartRotation = settleRotation;
          const nudgeEndRotation = settleRotation + nudgeDeg;
          let nudgeTickFired = false;
          group.start({
            durationMs: 400,
            easing: Easings.easeOutQuart,
            onUpdate: (p) => {
              const r = nudgeStartRotation + p * (nudgeEndRotation - nudgeStartRotation);
              setRotation(r);
              if (!nudgeTickFired && p > 0.5) {
                AudioBus.shared.play('wheel:tick', { volume: 0.5 });
                nudgeTickFired = true;
              }
            },
            onComplete: () => {
              AudioBus.shared.play('wheel:land', { volume: 0.45 });
              fireChipReveal();
            },
          });
        };

        // Action-card sequencer. Producer brief 2026-05-13: "the
        // dynamic of each player's special items being able to
        // affect one another is half the fun." Walk the server-
        // computed action cards one at a time, ~700ms each, so
        // every player sees the story of how items affected the
        // wheel before the per-seat outcome chips land. Empty list
        // → skip straight to the nudge + chips.
        // Filtered card list — `displayActionCards` already drops
        // per-rival LIGHTNING cards (they exist on the wire for the
        // OutcomeChip ledger but the central overlay should only
        // play the SUMMARY card per LIGHTNING deploy).
        const cards = displayActionCards;
        if (cards.length === 0) {
          // 300ms hold so the player sees the un-shifted landing,
          // then nudge (if any) + chips.
          window.setTimeout(fireBoostNudge, 300);
          return;
        }
        // Play each card for ACTION_CARD_DURATION_MS. Source-self
        // BOOST cards trigger the wheel nudge DURING the card
        // (concurrent so the visual + label land together); other
        // cards just flash.
        let cardIdx = 0;
        const showNext = () => {
          if (cardIdx >= cards.length) {
            setActionCardIndex(null);
            // Boost nudge already ran (if applicable) during the
            // BOOST card; jump to chip reveal directly.
            if (boostNudgeAlreadyFired) {
              fireChipReveal();
            } else {
              fireBoostNudge();
            }
            return;
          }
          setActionCardIndex(cardIdx);
          const card = cards[cardIdx]!;
          // Producer brief 2026-05-15 — when the MAGNET card comes
          // up, tween the wheel from baseline → final IN PARALLEL
          // with the card flash so the player sees the magnet
          // literally drag the top wedge onto the deployer's
          // pointer. Picks the shorter rotational path (the magnet
          // pull should look snappy, not a full lap). The chip
          // reveal still anchors on the final settleRotation.
          if (card.itemId === 'MAGNET' && magnetPullActive) {
            let delta = settleRotation - baselineSettleRotation;
            // Take the shorter rotational arc — magnet should pull
            // the wedge, not haul it around the long way.
            const mod360 = ((delta % 360) + 360) % 360;
            delta = mod360 > 180 ? mod360 - 360 : mod360;
            if (Math.abs(delta) > 0.5) {
              const pullStart = baselineSettleRotation;
              const pullEnd = baselineSettleRotation + delta;
              group.start({
                durationMs: 700,
                easing: Easings.easeOutBack,
                onUpdate: (p) => {
                  setRotation(pullStart + p * (pullEnd - pullStart));
                },
                onComplete: () => {
                  AudioBus.shared.play('wheel:land', { volume: 0.55 });
                  // Snap rotation onto the true settleRotation so the
                  // boost nudge below has the correct anchor (the
                  // easeOutBack overshoot can leave us a fraction off).
                  setRotation(settleRotation);
                },
              });
              AudioBus.shared.play('wheel:tick', { volume: 0.6 });
            }
          }
          // If THIS card is the self-seat's BOOST / TRIPLE_BOOST,
          // kick off the wheel nudge in parallel — wheel ticks one
          // (or three) wedges AS the card flashes "YOU BOOSTED".
          if (
            (card.itemId === 'BOOST' || card.itemId === 'TRIPLE_BOOST') &&
            selfSeatId !== null &&
            card.sourceSeatId === selfSeatId &&
            nudgeDeg !== 0 &&
            !boostNudgeAlreadyFired
          ) {
            boostNudgeAlreadyFired = true;
            const nudgeStartRotation = settleRotation;
            const nudgeEndRotation = settleRotation + nudgeDeg;
            let nudgeTickFired = false;
            group.start({
              durationMs: 400,
              easing: Easings.easeOutQuart,
              onUpdate: (p) => {
                const r = nudgeStartRotation + p * (nudgeEndRotation - nudgeStartRotation);
                setRotation(r);
                if (!nudgeTickFired && p > 0.5) {
                  AudioBus.shared.play('wheel:tick', { volume: 0.5 });
                  nudgeTickFired = true;
                }
              },
              onComplete: () => {
                AudioBus.shared.play('wheel:land', { volume: 0.45 });
              },
            });
          }
          AudioBus.shared.play('chip:drop', { volume: 0.4 });
          cardIdx += 1;
          window.setTimeout(showNext, ACTION_CARD_DURATION_MS);
        };
        let boostNudgeAlreadyFired = false;
        window.setTimeout(showNext, 300);
      },
    });

    return () => {
      group.cancelAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    settleRotation,
    baselineSettleRotation,
    magnetPullActive,
    isPreSpin,
    resolve,
    selfSeatId,
  ]);

  const { wheelSize, avatarDiameter, orbitRadius, stageSize, hubSize } = dims;
  const stageCenter = stageSize / 2;
  // The rim PNG has a transparent hole that occupies ~68% of its
  // image diameter, so a render at 1.0× wheelSize would hide the
  // gold band inside the wedges entirely. Render at ~1.45× wheelSize
  // so the rim's inner edge lines up with the wheel disc's outer
  // edge and the visible gold band wraps the full circumference.
  // Avatars (z-index 4) still sit above the rim (z-index 2) at the
  // orbit radius, so they read as beads on the gold ring.
  const rimDiameter = Math.round(wheelSize * 1.45);
  const rimLightsExtra = Math.round(wheelSize * 0.054); // ~14 at 260, ~25 at 460

  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        // BalancePill (top:14, ~44px) + ResolveBanner (top:70, now a
        // single-line ~25px chip after the 2026-05-12 declutter)
        // means the banner ends ~y=100. Top seat sits at stageCenter
        // - orbitRadius (≈ y=42 inside the stage), so top=120 gives
        // ~20px of clearance below the banner without wasting too
        // much vertical space.
        top: 120,
        transform: 'translateX(-50%)',
        width: stageSize,
        height: stageSize,
        zIndex: 2,
      }}
    >
      {/* Wheel disc — rotates via Tween-driven `rotation`. Wedge
       *  labels live INSIDE this div so they rotate with the wheel.
       *  Each label counter-rotates so its glyph stays upright. */}
      <div
        style={{
          position: 'absolute',
          left: stageCenter - wheelSize / 2,
          top: stageCenter - wheelSize / 2,
          width: wheelSize,
          height: wheelSize,
          borderRadius: '50%',
          background: `conic-gradient(${conicSegments})`,
          boxShadow:
            '0 0 0 4px rgba(0,0,0,0.5), 0 14px 32px rgba(0,0,0,0.6)',
          transform: `rotate(${rotation}deg)`,
          transformOrigin: 'center',
        }}
      >
        {dial.wedges.map((wedge, i) => (
          <WedgeLabel
            key={i}
            wedge={wedge}
            index={i}
            dims={dims}
            wheelRotationDeg={rotation}
          />
        ))}
        {/* BONUS_WEDGE preview markers — render inside the disc so
         *  they co-rotate with the wedges they replace. Producer
         *  brief 2026-05-15: every player should see the bonus
         *  wedge land on the dial the moment its deployer fires
         *  the item, with the deployer's avatar attached so the
         *  attribution is unmistakable. */}
        {bonusWedgePlacements.map((placement, idx) => {
          const deployer = seats.find(
            (s) => String(s.seatId) === String(placement.sourceSeatId),
          );
          return (
            <BonusWedgeMarker
              key={`bonus-${idx}-${placement.wedgeIndex}`}
              wedgeIndex={placement.wedgeIndex}
              deployerSeat={deployer ?? null}
              dims={dims}
              wheelRotationDeg={rotation}
              isSelf={
                selfSeatId !== null &&
                String(placement.sourceSeatId) === String(selfSeatId)
              }
            />
          );
        })}
      </div>

      {/* Commissioned outer rim — doesn't rotate. Sized so the gold
       *  band wraps the wheel's circumference (inner edge of the PNG
       *  hole lines up with the wheel disc's outer edge). Tailwind's
       *  preflight applies max-width:100% to every <img>; force it
       *  off so the rim renders at its intended diameter on any
       *  viewport. */}
      <img
        src={wheelOuterRimUrl}
        alt=""
        aria-hidden
        draggable={false}
        style={{
          position: 'absolute',
          left: stageCenter - rimDiameter / 2,
          top: stageCenter - rimDiameter / 2,
          width: rimDiameter,
          height: rimDiameter,
          maxWidth: 'none',
          maxHeight: 'none',
          pointerEvents: 'none',
          filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.45))',
          zIndex: 2,
        }}
      />

      {/* Rim chase lights. */}
      <RimLights
        diameter={wheelSize + rimLightsExtra}
        centerX={stageCenter}
        centerY={stageCenter}
        count={16}
        cycleMs={landed ? 3200 : 1600}
      />

      {/* Hub — sits centred over the disc. Layer order:
       *    1) Dark backdrop disc (behind the gold ring) — gives the
       *       countdown number a high-contrast surface to read
       *       against, rather than the green/blue wedge fills bleeding
       *       through the hub's transparent centre.
       *    2) Hub image — the gold ring (existing commissioned art).
       *    3) Text overlay — countdown number (large, during STAKE)
       *       + "Let's Go!" label.
       *  Producer brief 2026-05-15: "Move the countdown to the
       *  middle of the wheel, placing a disc behind this gold
       *  centre ring - with the countdown on top." */}
      <div
        style={{
          position: 'absolute',
          left: stageCenter - hubSize / 2,
          top: stageCenter - hubSize / 2,
          width: hubSize,
          height: hubSize,
          pointerEvents: 'none',
          zIndex: 3,
        }}
      >
        {/* 1) Backdrop disc — inset so the gold ring still reads as
         *    the visible frame. */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: hubSize * 0.07,
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 50% 35%, #2A1E63 0%, #16113D 55%, #0B0726 100%)',
            boxShadow: 'inset 0 4px 12px rgba(0,0,0,0.45)',
          }}
        />
        {/* 2) Hub image — gold ring frames the backdrop. */}
        <img
          src={wheelHubUrl}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.55))',
          }}
        />
        {/* 3) Text overlay — countdown digit (during STAKE) OR
         *    "Let's Go!" (after STAKE completes / wheel is about
         *    to spin). Producer brief 2026-05-15: "Let's Go would
         *    show once the countdown completes" — the two are
         *    sequential, never simultaneous. Sized to fit inside
         *    the gold ring's inner circle so the text doesn't
         *    spill past the chrome. */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FFE9A6',
            fontFamily: '"Fredoka", -apple-system, system-ui',
            textShadow: '0 2px 4px rgba(0,0,0,0.85)',
          }}
        >
          {isPreSpin ? (
            <div
              style={{
                fontSize: Math.round(hubSize * 0.42),
                fontWeight: 900,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1,
                color: stakeRemainingSec <= 2 ? '#FF8E9D' : '#FFE9A6',
              }}
            >
              {stakeRemainingSec}
            </div>
          ) : (
            <div
              style={{
                fontSize: Math.round(hubSize * 0.15),
                fontWeight: 900,
                letterSpacing: 0.5,
                lineHeight: 1.05,
                color: '#FFD86B',
                textShadow: '0 1px 2px rgba(0,0,0,0.85)',
                textAlign: 'center',
              }}
            >
              Let&rsquo;s
              <br />
              Go!
            </div>
          )}
        </div>
      </div>

      {/* Six pointer slots around the perimeter. Producer brief:
       *  nobody is seated at STAKE entry — slot 1..6 start empty and
       *  fill in over the window as the player + bots claim seats.
       *  For each slot we either render the seated avatar
       *  (SeatPointer) or an empty-slot marker (EmptySlotMarker)
       *  that's tappable for the local player during STAKE. The
       *  resolver fills any still-empty slots at WHEEL_RESOLVE entry
       *  so this loop always renders a full ring during the spin. */}
      {SEAT_POINTER_OFFSETS_DEG.map((offsetDeg, pointerIdx) => {
        const slot = pointerIdx + 1;
        const angleRad = ((offsetDeg - 90) * Math.PI) / 180;
        const cx = stageCenter + Math.cos(angleRad) * orbitRadius;
        const cy = stageCenter + Math.sin(angleRad) * orbitRadius;

        // Look up the seat occupying this slot, if any.
        const seatId = Object.entries(seatPointerSlots ?? {}).find(
          ([, s]) => Number(s) === slot,
        )?.[0];
        const seat = seatId
          ? seats.find((s) => s.seatId === Number(seatId)) ?? null
          : null;

        if (!seat) {
          // Empty slot — tappable for the local player during STAKE.
          const canClaim = isPreSpin && !!roomId && !!playerId;
          const onTapEmpty = canClaim
            ? () => {
                void submitWheelCommand(
                  roomId,
                  'SELECT_SEAT',
                  playerId,
                  { slot },
                ).catch((e) =>
                  console.warn('[WheelResolveView] SELECT_SEAT failed', e),
                );
              }
            : null;
          return (
            <EmptySlotMarker
              key={`empty-${slot}`}
              slot={slot}
              cx={cx}
              cy={cy}
              avatarDiameter={avatarDiameter}
              onTap={onTapEmpty}
            />
          );
        }

        const isSelf = selfSeatId !== null && seat.seatId === selfSeatId;
        const outcome = resolve?.perSeatOutcomes[String(seat.seatId)] ?? null;
        const revealed = revealedSeatIds.has(seat.seatId);
        const onTap =
          isPreSpin && roomId && playerId && !isSelf
            ? () => {
                void submitWheelCommand(roomId, 'SELECT_SEAT', playerId, {
                  slot,
                }).catch((e) =>
                  console.warn('[WheelResolveView] SELECT_SEAT failed', e),
                );
              }
            : null;

        return (
          <SeatPointer
            key={seat.seatId}
            seat={seat}
            cx={cx}
            cy={cy}
            pointerAngleDeg={offsetDeg}
            outcome={outcome}
            actionCards={resolve?.actionCards ?? []}
            isSelf={isSelf}
            revealed={revealed}
            landed={landed}
            avatarDiameter={avatarDiameter}
            chipMinWidth={dims.outcomeChipMinWidth}
            chipFontSize={dims.outcomeChipFontSize}
            interactive={isPreSpin}
            onTap={onTap}
          />
        );
      })}

      {/* STAKE-window clock-fill ring + numeric countdown.
       *  – Ring overlays the avatar orbit.
       *  – Countdown + "Tap a seat · Use items" hint render BELOW the
       *    stage so the bottom seat avatar isn't clipped (the older
       *    in-stage placement sat at {@code bottom: -8}, which
       *    overlapped the bottom seat's marker on mobile). */}
      {isPreSpin ? (
        <>
          <StakeOverlay
            stageCenter={stageCenter}
            orbitRadius={orbitRadius}
            avatarDiameter={avatarDiameter}
            progress={stakeProgress}
          />
          {/* Countdown number lives in the wheel hub now (producer
           *  brief 2026-05-15). The clock-fill ring above still
           *  surfaces the time-remaining as a circular sweep, and
           *  the {@link StakeCountdownBelow} hint below keeps the
           *  "Tap a seat · Use items" instruction without the
           *  duplicate digit. */}
          <StakeCountdownBelow
            top={stageSize + 8}
            remainingSec={stakeRemainingSec}
          />
        </>
      ) : null}

      {/* Confetti burst on landing — WHEEL_RESOLVE only. */}
      {!isPreSpin && landed ? (
        <ConfettiBurst centerX={stageCenter} centerY={stageCenter} />
      ) : null}

      {/* Per-avatar item effects — the bomb dropping, lightning
       *  striking, shield bubbling. Rendered ON the affected seat
       *  avatars around the wheel rim so the player sees who is
       *  being acted on directly, not via a central icon. The card
       *  overlay below handles only the "who did what" headline. */}
      {actionCardIndex !== null && displayActionCards[actionCardIndex] ? (
        <SeatItemFxLayer
          card={displayActionCards[actionCardIndex]!}
          allCards={resolve?.actionCards ?? []}
          seats={seats}
          seatPointerSlots={seatPointerSlots ?? {}}
          stageCenter={stageCenter}
          orbitRadius={orbitRadius}
          avatarDiameter={avatarDiameter}
        />
      ) : null}

      {/* Action-card overlay — flashes once per item deploy between
       *  the wheel-land and the per-seat chip reveal. Compact pill
       *  at the wheel hub now; the per-item animation lives on the
       *  affected seat avatars above. */}
      {actionCardIndex !== null && displayActionCards[actionCardIndex] ? (
        <ActionCardOverlay
          card={displayActionCards[actionCardIndex]!}
          seats={seats}
          selfSeatId={selfSeatId}
          stageCenter={stageCenter}
        />
      ) : null}
    </div>
  );
}

/** Computes which seats an action card affects — the seats whose
 *  avatars should animate when the card plays.
 *
 *  - BOOST / TRIPLE_BOOST: just the deployer (self-buff)
 *  - HIT / BOMB / SHAKEDOWN: the named target
 *  - POW / LIGHTNING: every non-source non-EMPTY seat (mass effect)
 */
function affectedSeatsForCard(
  card: WheelActionCard,
  seats: ReadonlyArray<WheelSeatView>,
): number[] {
  switch (card.itemId) {
    case 'BOOST':
    case 'TRIPLE_BOOST':
    case 'MAGNET':
    case 'X2_BONUS':
    case 'COIN_STASH':
    case 'BONUS_WEDGE':
      return [card.sourceSeatId];
    case 'HIT':
    case 'BOMB':
      return card.targetSeatId !== null ? [card.targetSeatId] : [];
    case 'SHAKEDOWN':
      // Producer brief 2026-05-16 — SHAKEDOWN is a transfer; the
      // source picks up the stolen coins, so they get a green +N
      // floater + gain-flavoured animation alongside the target's
      // red −N.
      return card.targetSeatId !== null
        ? [card.sourceSeatId, card.targetSeatId]
        : [];
    case 'POW':
      // Pure damage — no transfer. Affects rivals only; source has
      // no on-avatar beat.
      return seats
        .filter(
          (s) =>
            s.seatId !== card.sourceSeatId && s.kind !== 'EMPTY',
        )
        .map((s) => s.seatId);
    case 'LIGHTNING':
      // Producer brief 2026-05-16 — LIGHTNING transfers half of every
      // rival's payout to the deployer, so the source needs a beat
      // too: green +N floater matching the red −N on every rival.
      return seats
        .filter((s) => s.kind !== 'EMPTY')
        .map((s) => s.seatId);
    default:
      return [];
  }
}

/** Mass renders {@link SeatItemFx} on every affected avatar for the
 *  currently-playing action card. The FX layer sits between the
 *  seat avatars and the central headline pill in z-order, so the
 *  bomb dropping on your avatar is visible without occluding the
 *  wheel rim or the headline.
 *
 *  <p>For each affected seat, also renders a floating coin-delta
 *  text below the avatar (e.g. "−5") so the player sees the impact
 *  per rival, not just an aggregate. Producer brief 2026-05-14:
 *  "Lightning strikes the player, we see the −5 coins underneath."
 *  Pulls the per-seat delta from {@code allCards} (resolver emits
 *  per-rival LIGHTNING cards alongside the SUMMARY card; the
 *  display sequencer filters those out for the central overlay but
 *  this layer needs them for the per-avatar floaters). */
function SeatItemFxLayer({
  card,
  allCards,
  seats,
  seatPointerSlots,
  stageCenter,
  orbitRadius,
  avatarDiameter,
}: {
  card: WheelActionCard;
  allCards: ReadonlyArray<WheelActionCard>;
  seats: ReadonlyArray<WheelSeatView>;
  seatPointerSlots: Record<string, number> | Record<string, string>;
  stageCenter: number;
  orbitRadius: number;
  avatarDiameter: number;
}) {
  const affected = affectedSeatsForCard(card, seats);
  return (
    <>
      {affected.map((seatId) => {
        // Look up which pointer slot this seat occupies, then
        // resolve that to a screen position around the orbit.
        const slot = Number(
          (seatPointerSlots as Record<string, number | string>)[String(seatId)] ?? seatId,
        );
        const offsetDeg = SEAT_POINTER_OFFSETS_DEG[(slot - 1) % 6] ?? 0;
        const angleRad = ((offsetDeg - 90) * Math.PI) / 180;
        const cx = stageCenter + Math.cos(angleRad) * orbitRadius;
        const cy = stageCenter + Math.sin(angleRad) * orbitRadius;
        const isTarget =
          card.targetSeatId !== null && card.targetSeatId === seatId;
        const isSource = card.sourceSeatId === seatId;
        // Per-seat delta: how many coins this seat actually
        // lost/gained because of THIS action card. Lookups vary by
        // card type — for mass-effect LIGHTNING (target=null) the
        // resolver emits per-rival sibling cards we can scan.
        const seatDelta = lookupDeltaForSeat(card, seatId, allCards);
        return (
          <SeatItemFx
            key={`${card.sourceSeatId}-${card.itemId}-${card.targetSeatId ?? 'm'}-${card.blocked ? 'b' : 'l'}-${seatId}`}
            cx={cx}
            cy={cy}
            avatarDiameter={avatarDiameter}
            card={card}
            isTarget={isTarget}
            isSource={isSource}
            seatDelta={seatDelta}
          />
        );
      })}
    </>
  );
}

/** Find the coin delta to display under a specific seat's avatar
 *  while {@code card} plays. Walks {@code allCards} so mass-effect
 *  summary cards (LIGHTNING target=null) can resolve each affected
 *  rival's individual loss via the per-rival sibling cards the
 *  resolver emits alongside.
 *
 *  <p>Returns 0 when nothing should display (BLOCKED hits, SHIELD
 *  beats, or a seat that wasn't in this card's affected list). */
function lookupDeltaForSeat(
  card: WheelActionCard,
  seatId: number,
  allCards: ReadonlyArray<WheelActionCard>,
): number {
  if (card.blocked) return 0;
  // Source-side cards (BOOST, TRIPLE_BOOST, X2_BONUS, COIN_STASH):
  // the affected seat IS the source.
  if (card.sourceSeatId === seatId && card.sourceDelta !== 0) {
    return card.sourceDelta;
  }
  // Target-side cards (HIT, BOMB, SHAKEDOWN target): single target.
  if (card.targetSeatId === seatId && card.targetDelta !== 0) {
    return card.targetDelta;
  }
  // SHAKEDOWN — source ALSO gains. Show that on the source avatar.
  if (
    card.itemId === 'SHAKEDOWN' &&
    card.sourceSeatId === seatId &&
    card.sourceDelta !== 0
  ) {
    return card.sourceDelta;
  }
  // Mass-effect LIGHTNING / POW — summary card has target=null.
  // For each affected rival, scan for the per-rival sibling card
  // emitted by the resolver with matching source + this rival as
  // target.
  if (
    (card.itemId === 'LIGHTNING' || card.itemId === 'POW') &&
    card.targetSeatId === null
  ) {
    const sibling = allCards.find(
      (c) =>
        c.itemId === card.itemId &&
        c.sourceSeatId === card.sourceSeatId &&
        c.targetSeatId === seatId,
    );
    if (sibling) return sibling.targetDelta;
  }
  return 0;
}

/** A single per-avatar item effect — the bomb falling, lightning
 *  striking, shield bubbling, etc. Mounts at the affected seat's
 *  (cx, cy) for the lifetime of the action card it's tied to.
 *  Renders ON TOP of the avatar (item icon + per-item motion); the
 *  blocked variant overlays a cyan shield bubble that absorbs the
 *  hit visually. */
function SeatItemFx({
  cx,
  cy,
  avatarDiameter,
  card,
  isTarget,
  isSource,
  seatDelta,
}: {
  cx: number;
  cy: number;
  avatarDiameter: number;
  card: WheelActionCard;
  isTarget: boolean;
  isSource: boolean;
  /** Coin-delta this seat experiences from this card. Rendered as
   *  a floating "+N" / "−N" below the avatar. 0 = no floater (e.g.
   *  BLOCKED hit). */
  seatDelta: number;
}) {
  const iconUrl = ITEM_ICON_URL_BY_ID[card.itemId] ?? null;
  // Pick the animation. For sabotage on a shielded target the
  // SHIELD bubble takes over visually — the hit / bomb icon still
  // flashes but the bubble's the focal point.
  //
  // Producer brief 2026-05-16 — transfer items (LIGHTNING,
  // SHAKEDOWN) play a "gain" beat on the source's avatar so the
  // green floater reads as a positive collection rather than the
  // bolt strike / mugging animation the rivals are seeing.
  const isTransferSource =
    isSource &&
    seatDelta > 0 &&
    (card.itemId === 'LIGHTNING' || card.itemId === 'SHAKEDOWN');
  const animName = (() => {
    if (isTransferSource) return 'seatfx-coin-stash';
    switch (card.itemId) {
      case 'BOOST':
      case 'TRIPLE_BOOST':
        return 'seatfx-boost';
      case 'HIT':
        return 'seatfx-hit';
      case 'BOMB':
        return 'seatfx-bomb';
      case 'POW':
        return 'seatfx-pow';
      case 'LIGHTNING':
        return 'seatfx-lightning';
      case 'SHAKEDOWN':
        return 'seatfx-shakedown';
      case 'MAGNET':
        return 'seatfx-magnet';
      case 'BONUS_WEDGE':
        return 'seatfx-bonus-wedge';
      // Producer brief 2026-05-16 — fill in bespoke beats for the
      // remaining wired items so every deploy has a distinct
      // on-avatar animation rather than the generic scale pulse.
      case 'COIN_STASH':
        return 'seatfx-coin-stash';
      case 'SHIELD':
        return 'seatfx-shield';
      case 'X2_BONUS':
        return 'seatfx-x2-bonus';
      default:
        return 'seatfx-default';
    }
  })();

  const iconSize = avatarDiameter * 0.85;
  const ringSize = avatarDiameter * 1.4;

  return (
    <>
      {/* Item icon — drops/strikes/pulses on the affected avatar. */}
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            position: 'absolute',
            top: cy - iconSize / 2,
            left: cx - iconSize / 2,
            width: iconSize,
            height: iconSize,
            objectFit: 'contain',
            zIndex: 5,
            pointerEvents: 'none',
            filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.65))',
            animation: `${animName} 1500ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
            opacity: 0,
            willChange: 'transform, opacity',
          }}
        />
      ) : null}
      {/* Impact ring — coloured per item kind, pulses out from the
       *  avatar so the eye lands on this seat. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: cy - ringSize / 2,
          left: cx - ringSize / 2,
          width: ringSize,
          height: ringSize,
          borderRadius: '50%',
          border: `2px solid ${impactRingColor(card)}`,
          boxShadow: `0 0 16px ${impactRingColor(card)}aa, inset 0 0 12px ${impactRingColor(card)}55`,
          zIndex: 5,
          pointerEvents: 'none',
          animation: `seatfx-ring 1500ms ease-out forwards`,
          opacity: 0,
          willChange: 'transform, opacity',
        }}
      />
      {/* Coin-delta floater — "−5" or "+80" lifts below the
       *  affected avatar so the player sees the exact impact this
       *  card has on each player. Producer brief 2026-05-14:
       *  "Lightning strikes the player, we see the −5 coins
       *  underneath." Skipped on zero (blocked hits, neutral
       *  effects). */}
      {seatDelta !== 0 ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: cy + avatarDiameter * 0.55,
            left: cx,
            transform: 'translate(-50%, 0)',
            zIndex: 7,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 11px',
            borderRadius: 999,
            background: 'rgba(6, 13, 34, 0.94)',
            border: `1.5px solid ${
              seatDelta > 0 ? '#9DE54E' : '#FF6F6F'
            }`,
            boxShadow: '0 3px 10px rgba(0,0,0,0.55)',
            color: seatDelta > 0 ? '#D4FF99' : '#FF8E9D',
            fontFamily: '"Fredoka", -apple-system, system-ui',
            fontSize: 13,
            fontWeight: 900,
            letterSpacing: 0.5,
            textShadow: '0 1px 2px rgba(0,0,0,0.85)',
            whiteSpace: 'nowrap',
            animation: 'seatfx-coin-delta 2500ms ease-out forwards',
            opacity: 0,
            willChange: 'transform, opacity',
          }}
        >
          {/* Coin glyph */}
          <Icon name="coin" size={14} alt="coins" />
          {/* Direction arrow — producer brief 2026-05-14: "coins &
              red number with down arrow". Up-arrow on gains so the
              visual language stays consistent across sign. */}
          <span style={{ fontSize: 14, lineHeight: 1, fontWeight: 900 }}>
            {seatDelta > 0 ? '▲' : '▼'}
          </span>
          <span>{Math.abs(seatDelta)}</span>
        </div>
      ) : null}
      {/* SHIELD bubble — pops over the affected avatar when a
       *  sabotage was absorbed. Lives ABOVE the item icon so it
       *  reads as catching the projectile. Only fires when this
       *  avatar IS the target AND the card is blocked. */}
      {card.blocked && isTarget ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: cy - (avatarDiameter * 0.7),
            left: cx - (avatarDiameter * 0.7),
            width: avatarDiameter * 1.4,
            height: avatarDiameter * 1.4,
            borderRadius: '50%',
            border: '3px solid #5DD5FF',
            boxShadow:
              'inset 0 0 14px rgba(93,213,255,0.7), 0 0 22px rgba(93,213,255,0.85)',
            zIndex: 6,
            pointerEvents: 'none',
            animation: 'seatfx-shield 1500ms ease-out forwards',
            opacity: 0,
            willChange: 'transform, opacity',
          }}
        />
      ) : null}
      <style>{`
        @keyframes seatfx-boost {
          0%   { transform: translateY(28px) scale(0.6); opacity: 0; }
          25%  { transform: translateY(-10px) scale(1.1); opacity: 1; }
          50%  { transform: translateY(-18px) scale(1); opacity: 1; }
          85%  { transform: translateY(-22px) scale(1); opacity: 1; }
          100% { transform: translateY(-26px) scale(0.9); opacity: 0; }
        }
        @keyframes seatfx-hit {
          0%   { transform: scale(0.4) rotate(-25deg); opacity: 0; }
          20%  { transform: scale(1.4) rotate(8deg); opacity: 1; }
          35%  { transform: scale(1) rotate(0deg); opacity: 1; }
          85%  { transform: scale(1) rotate(0deg); opacity: 1; }
          100% { transform: scale(0.85) rotate(0deg); opacity: 0; }
        }
        @keyframes seatfx-bomb {
          0%   { transform: translateY(-60px) rotate(-12deg); opacity: 0; }
          25%  { transform: translateY(-4px) rotate(0deg); opacity: 1; }
          35%  { transform: translateY(0) scale(1.18); opacity: 1; }
          50%  { transform: translateY(0) scale(1); opacity: 1; }
          85%  { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(0) scale(0.9); opacity: 0; }
        }
        @keyframes seatfx-pow {
          0%   { transform: scale(0.3) rotate(-20deg); opacity: 0; }
          20%  { transform: scale(1.4) rotate(0deg); opacity: 1; }
          35%  { transform: scale(1) rotate(0deg); opacity: 1; }
          85%  { transform: scale(1) rotate(0deg); opacity: 1; }
          100% { transform: scale(0.9) rotate(0deg); opacity: 0; }
        }
        @keyframes seatfx-lightning {
          0%   { transform: translateY(-72px) scaleY(0.3); opacity: 0; }
          12%  { transform: translateY(0) scaleY(1.35); opacity: 1; filter: brightness(2.3) drop-shadow(0 0 14px #fff); }
          24%  { transform: translateY(0) scaleY(1); opacity: 1; filter: brightness(1); }
          50%  { transform: translateY(0) scaleY(1); opacity: 1; }
          85%  { transform: translateY(0) scaleY(1); opacity: 1; }
          100% { transform: translateY(0) scaleY(0.95); opacity: 0; }
        }
        @keyframes seatfx-shakedown {
          0%   { transform: translateX(0) rotate(0); opacity: 0; }
          15%  { transform: translateX(-6px) rotate(-8deg); opacity: 1; }
          30%  { transform: translateX(6px) rotate(8deg); opacity: 1; }
          45%  { transform: translateX(-5px) rotate(-6deg); }
          60%  { transform: translateX(5px) rotate(6deg); }
          85%  { transform: translateX(0) rotate(0); opacity: 1; }
          100% { transform: translateX(0) rotate(0); opacity: 0; }
        }
        @keyframes seatfx-magnet {
          /* Magnet drifts in from above, settles with a pulse —
             "pulled toward the top of the wheel" beat. */
          0%   { transform: translateY(-28px) scale(0.7) rotate(-15deg); opacity: 0; }
          25%  { transform: translateY(-8px) scale(1.2) rotate(0deg); opacity: 1; }
          40%  { transform: translateY(0) scale(1) rotate(0deg); opacity: 1; }
          55%  { transform: translateY(-3px) scale(1.08) rotate(0deg); opacity: 1; }
          70%  { transform: translateY(0) scale(1) rotate(0deg); opacity: 1; }
          85%  { transform: translateY(0) scale(1) rotate(0deg); opacity: 1; }
          100% { transform: translateY(-4px) scale(0.9) rotate(0deg); opacity: 0; }
        }
        @keyframes seatfx-bonus-wedge {
          /* Sparkle bloom — appears on the deployer's avatar then
             eases up as the wheel-bonus visually "flies to the
             dial". The actual wedge replacement happens server-side;
             this is the narrative beat. */
          0%   { transform: scale(0.4) rotate(0deg); opacity: 0; }
          20%  { transform: scale(1.3) rotate(20deg); opacity: 1; }
          35%  { transform: scale(1) rotate(0deg); opacity: 1; }
          55%  { transform: translateY(-8px) scale(0.95) rotate(-10deg); opacity: 1; }
          80%  { transform: translateY(-18px) scale(0.85) rotate(8deg); opacity: 0.85; }
          100% { transform: translateY(-30px) scale(0.6) rotate(-6deg); opacity: 0; }
        }
        @keyframes seatfx-coin-stash {
          /* Coin pile lands on the avatar — slight bounce-stack
             with a yellow "stacking" wobble so the +50 reads as
             "your bank just grew". */
          0%   { transform: translateY(-22px) scale(0.7); opacity: 0; }
          25%  { transform: translateY(2px) scale(1.18); opacity: 1; }
          40%  { transform: translateY(0) scale(1); opacity: 1; }
          55%  { transform: translateY(-2px) scale(1.06); opacity: 1; }
          70%  { transform: translateY(0) scale(1); opacity: 1; }
          85%  { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(0) scale(0.9); opacity: 0; }
        }
        @keyframes seatfx-x2-bonus {
          /* ×2 badge swells in with a slight 8° spin, holds, eases
             out — the "your winnings doubled" callout. */
          0%   { transform: scale(0.5) rotate(-8deg); opacity: 0; }
          20%  { transform: scale(1.3) rotate(8deg); opacity: 1; }
          35%  { transform: scale(1) rotate(0deg); opacity: 1; }
          55%  { transform: scale(1.08) rotate(-4deg); opacity: 1; }
          70%  { transform: scale(1) rotate(0deg); opacity: 1; }
          100% { transform: scale(0.9) rotate(0deg); opacity: 0; }
        }
        @keyframes seatfx-default {
          0%   { transform: scale(0.6); opacity: 0; }
          25%  { transform: scale(1.18); opacity: 1; }
          40%  { transform: scale(1); opacity: 1; }
          85%  { transform: scale(1); opacity: 1; }
          100% { transform: scale(0.9); opacity: 0; }
        }
        @keyframes seatfx-ring {
          0%   { transform: scale(0.4); opacity: 0; }
          20%  { transform: scale(1.05); opacity: 0.9; }
          45%  { transform: scale(1.18); opacity: 0.6; }
          100% { transform: scale(1.4); opacity: 0; }
        }
        @keyframes seatfx-shield {
          0%   { transform: scale(0.6); opacity: 0; }
          18%  { transform: scale(1.18); opacity: 1; }
          35%  { transform: scale(1); opacity: 1; }
          85%  { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.05); opacity: 0.7; }
        }
        @keyframes seatfx-coin-delta {
          0%   { transform: translate(-50%, 12px) scale(0.6); opacity: 0; }
          15%  { transform: translate(-50%, 0) scale(1.15); opacity: 1; }
          25%  { transform: translate(-50%, 0) scale(1); opacity: 1; }
          85%  { transform: translate(-50%, 0) scale(1); opacity: 1; }
          100% { transform: translate(-50%, -8px) scale(0.9); opacity: 0; }
        }
      `}</style>
    </>
  );
}

/** Per-card ring colour used by the impact halo behind the item
 *  icon on each affected avatar. Mirrors the card-overlay palette
 *  so the central headline + the on-avatar effects share a visual
 *  identity per item kind. */
function impactRingColor(card: WheelActionCard): string {
  if (card.blocked) return '#5DD5FF';
  switch (card.itemId) {
    case 'BOOST':
    case 'TRIPLE_BOOST':
    case 'COIN_STASH':
    case 'MAGNET':
      return '#FFD96B'; // warm gold — self-gain
    case 'HIT':
    case 'BOMB':
    case 'POW':
      return '#FF6F6F'; // red — sabotage
    case 'LIGHTNING':
    case 'SHAKEDOWN':
    case 'X2_BONUS':
      return '#B475FF'; // electric purple — payout-mod / multiplier
    case 'BONUS_WEDGE':
      return '#9DE54E'; // green — shared bonus, broadly positive
    default:
      return '#FFD96B';
  }
}

/** STAKE clock-fill ring around the wheel rim. The numeric countdown
 *  + "Tap a seat · Use items" hint used to live inside this overlay
 *  (anchored to the bottom of the stage), but it ended up clipping
 *  the bottom seat avatar — see {@link StakeCountdownBelow} for the
 *  new placement just below the wheel disc. */
function StakeOverlay({
  stageCenter,
  orbitRadius,
  avatarDiameter,
  progress,
}: {
  stageCenter: number;
  orbitRadius: number;
  avatarDiameter: number;
  progress: number;
}) {
  const ringInner = orbitRadius + avatarDiameter * 0.6;
  const ringOuter = ringInner + Math.max(8, avatarDiameter * 0.12);
  const filledDeg = Math.round(progress * 360);
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: stageCenter - ringOuter,
        top: stageCenter - ringOuter,
        width: ringOuter * 2,
        height: ringOuter * 2,
        borderRadius: '50%',
        background: `conic-gradient(
          from -90deg,
          rgba(255,228,138,0.85) ${filledDeg}deg,
          rgba(255,255,255,0.14) ${filledDeg}deg
        )`,
        maskImage: `radial-gradient(circle, transparent ${ringInner}px, #000 ${ringInner}px, #000 ${ringOuter}px, transparent ${ringOuter}px)`,
        WebkitMaskImage: `radial-gradient(circle, transparent ${ringInner}px, #000 ${ringInner}px, #000 ${ringOuter}px, transparent ${ringOuter}px)`,
        filter: 'drop-shadow(0 0 8px rgba(255,228,138,0.45))',
        pointerEvents: 'none',
        zIndex: 4,
      }}
    />
  );
}

/** Countdown number + "Tap a seat · Use items" hint, anchored just
 *  below the wheel disc. Producer brief 2026-05-12: the original
 *  position (inside the stage at {@code bottom: -8}) ended up
 *  clipping the bottom seat avatar, especially with the smaller
 *  mobile dims. Rendered as a sibling of {@code WheelStage}, so its
 *  vertical position is independent of the disc's stacking context. */
function StakeCountdownBelow({
  top,
  remainingSec,
}: {
  top: number;
  remainingSec: number;
}) {
  // The countdown number now lives in the wheel hub (producer brief
  // 2026-05-15). This component keeps only the hint text below the
  // wheel so the bottom seat avatars aren't obscured by a redundant
  // digit. {@code remainingSec} retained on the signature for
  // future "urgent flash <2s" treatments.
  void remainingSec;
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        top,
        textAlign: 'center',
        zIndex: 5,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          fontSize: 11,
          opacity: 0.85,
          letterSpacing: 0.5,
          color: '#E9F0FF',
          textShadow: '0 1px 2px rgba(0,0,0,0.85)',
        }}
      >
        Tap a seat · Use items
      </div>
    </div>
  );
}

/** Action-card flash — one card per interactive item deploy this
 *  round (producer brief 2026-05-13). Centred over the wheel hub.
 *  Each card shows the source player's avatar, the action verb +
 *  item icon, and (for targeted items) the target's avatar — so
 *  the player can immediately read "who did what to whom" without
 *  parsing text. Per-item animation drives the moment: bomb drops
 *  onto the target, lightning strikes from above, shield bubbles
 *  appear when blocked, etc. */
function ActionCardOverlay({
  card,
  seats,
  selfSeatId,
  stageCenter,
}: {
  card: WheelActionCard;
  seats: ReadonlyArray<WheelSeatView>;
  selfSeatId: number | null;
  stageCenter: number;
}) {
  const sourceSeat = seats.find((s) => s.seatId === card.sourceSeatId) ?? null;
  const targetSeat =
    card.targetSeatId !== null
      ? seats.find((s) => s.seatId === card.targetSeatId) ?? null
      : null;
  const sourceIsSelf = sourceSeat?.seatId === selfSeatId;
  const targetIsSelf = targetSeat?.seatId === selfSeatId;
  const sourceName = sourceIsSelf
    ? 'You'
    : (sourceSeat?.displayName ?? `Seat ${card.sourceSeatId}`);
  const targetName = targetIsSelf
    ? 'you'
    : (targetSeat?.displayName ?? null);

  // Colour palette by item-kind. Buffs read gold-warm, sabotage red,
  // SHIELD-blocked cyan, payout-modifiers electric purple.
  const isSabotage =
    card.itemId === 'HIT' ||
    card.itemId === 'BOMB' ||
    card.itemId === 'POW';
  const isPayoutMod =
    card.itemId === 'LIGHTNING' || card.itemId === 'SHAKEDOWN';
  const accent = card.blocked
    ? '#5DD5FF' // shield cyan
    : isSabotage
      ? '#FF6F6F' // red — sabotage hot
      : isPayoutMod
        ? '#B475FF' // electric purple — payout mod
        : '#FFD96B'; // gold — boost / triple-boost / fallback

  // Active-voice headlines that change subject based on who's
  // affected. "Lightning strike from Sam", "Bea bomb you", "You
  // shielded the bomb" — direct producer-brief phrasings.
  const headlineText = (() => {
    switch (card.itemId) {
      case 'BOOST':
        return sourceIsSelf ? 'You boosted' : `${sourceName} boosted`;
      case 'TRIPLE_BOOST':
        return sourceIsSelf
          ? 'You 3× boosted'
          : `${sourceName} 3× boosted`;
      case 'HIT':
        if (card.blocked) {
          return targetIsSelf
            ? `You shielded ${sourceName}'s hit`
            : `${targetName} shielded ${sourceName}'s hit`;
        }
        return sourceIsSelf
          ? `You hit ${targetName ?? '?'}`
          : `${sourceName} hit ${targetName ?? '?'}`;
      case 'BOMB':
        if (card.blocked) {
          return targetIsSelf
            ? `You shielded the bomb`
            : `${targetName} shielded the bomb`;
        }
        return sourceIsSelf
          ? `You bomb ${targetName ?? '?'}`
          : `${sourceName} bomb ${targetName ?? '?'}`;
      case 'POW':
        return sourceIsSelf
          ? 'Your POW hits all rivals'
          : `POW from ${sourceName} hits all rivals`;
      case 'LIGHTNING':
        return sourceIsSelf
          ? 'Your lightning halves rivals'
          : `Lightning strike from ${sourceName}`;
      case 'SHAKEDOWN':
        return sourceIsSelf
          ? `You shake down ${targetName ?? '?'}`
          : `${sourceName} shakes down ${targetName ?? '?'}`;
      case 'MAGNET':
        // Single-player card — only the deployer sees this beat.
        // The display-filter upstream guarantees sourceIsSelf, but
        // keep the rival-side string defensive.
        return sourceIsSelf
          ? 'Magnet locks onto the top wedge'
          : `${sourceName} magneted to the top wedge`;
      case 'X2_BONUS':
        return sourceIsSelf
          ? 'Your winnings doubled'
          : `${sourceName} doubled their winnings`;
      case 'COIN_STASH':
        return sourceIsSelf
          ? 'Coin Stash · +50 coins'
          : `${sourceName} stashed +50 coins`;
      case 'BONUS_WEDGE':
        return sourceIsSelf
          ? 'You added a bonus wedge'
          : `${sourceName} added a bonus wedge`;
      default:
        return `${sourceName} · ${card.itemId}`;
    }
  })();

  // Unique per-card animation key so consecutive cards each get a
  // fresh CSS animation (avoids the second card inheriting the
  // first's end-state and looking static).
  const key = `${card.sourceSeatId}-${card.itemId}-${card.targetSeatId ?? 'self'}-${card.blocked ? 'b' : 'l'}`;
  const pipSize = 32;

  return (
    <div
      key={key}
      aria-hidden
      style={{
        position: 'absolute',
        top: stageCenter,
        left: stageCenter,
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px 6px 6px',
        borderRadius: 999,
        background: 'rgba(6, 13, 34, 0.94)',
        border: `2px solid ${accent}`,
        boxShadow: `0 6px 22px rgba(0,0,0,0.7), 0 0 22px ${accent}55`,
        animation: `wheel-action-card-frame-${key} ${ACTION_CARD_DURATION_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
        pointerEvents: 'none',
        willChange: 'transform, opacity',
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      {sourceSeat ? (
        <AvatarPip
          seat={sourceSeat}
          size={pipSize}
          highlight={accent}
          isSelf={!!sourceIsSelf}
        />
      ) : null}
      <div
        style={{
          color: accent,
          fontFamily: '"Fredoka", -apple-system, system-ui',
          fontSize: 12,
          fontWeight: 900,
          letterSpacing: 0.5,
          textShadow: '0 1px 2px rgba(0,0,0,0.7)',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          padding: '0 2px',
        }}
      >
        {headlineText}
      </div>
      {targetSeat ? (
        <AvatarPip
          seat={targetSeat}
          size={pipSize}
          highlight={card.blocked ? '#5DD5FF' : '#FF6F6F'}
          isSelf={!!targetIsSelf}
        />
      ) : null}
      <style>{`
        @keyframes wheel-action-card-frame-${key} {
          0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
          12%  { opacity: 1; transform: translate(-50%, -50%) scale(1.06); }
          20%  { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          82%  { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(0.94); }
        }
      `}</style>
    </div>
  );
}

/** A circular avatar pip used inside the action card. Wraps
 *  {@link SeatAvatar} with a coloured ring so the card emphasises
 *  who is acting / being acted on. */
function AvatarPip({
  seat,
  size,
  highlight,
  isSelf,
}: {
  seat: WheelSeatView;
  size: number;
  highlight: string;
  isSelf: boolean;
}) {
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        boxShadow: isSelf
          ? `0 0 0 2px ${highlight}, 0 0 14px ${highlight}88, 0 3px 6px rgba(0,0,0,0.45)`
          : `0 0 0 2px ${highlight}, 0 3px 6px rgba(0,0,0,0.45)`,
        background: '#0B1838',
      }}
    >
      <SeatAvatar seat={seat} size={size} />
    </div>
  );
}

/** STAKE-phase item-deploy strip. Producer brief 2026-05-12: "it's
 *  not possible to use the items I've collected." Lives below the
 *  wheel during STAKE — one tile per unique item the player owns,
 *  showing the commissioned icon (item:{ID}).
 *
 *  <p>Phase 3.A gesture: two-tap commit. First tap arms the tile
 *  (lifts, shows a READY badge, dims its siblings); second tap on
 *  the same tile fires {@code DEPLOY_ITEM}. Tapping a DIFFERENT
 *  tile re-arms to that one. Tapping the surrounding backdrop
 *  cancels. Once fired, the tile flash-collapses; the deployed
 *  count comes back via the SSE delta and the remaining count
 *  decrements.
 *
 *  <p>Resolves to {@code null} for audience seats, seats with no
 *  loadout, or non-STAKE phases. */
function ItemDeployStrip({
  state,
  selfSeatId,
  playerId,
  roomId,
  stageSize,
}: {
  state: WheelObservableState;
  selfSeatId: number | null;
  playerId: string | null;
  roomId: string | null;
  stageSize: number;
}) {
  const selfSeat = useMemo(() => {
    if (selfSeatId === null) return null;
    return state.seats.find((s) => s.seatId === selfSeatId) ?? null;
  }, [state.seats, selfSeatId]);
  const [armedItem, setArmedItem] = useState<ItemId | null>(null);
  const [firingItem, setFiringItem] = useState<ItemId | null>(null);

  // Cancel any armed/firing state if the phase advances out of STAKE
  // (the strip unmounts in that case, but defensive — also catches a
  // server-side auto-deploy mid-arm that we don't want to keep showing
  // a stale "READY" for).
  useEffect(() => {
    return () => {
      setArmedItem(null);
      setFiringItem(null);
    };
  }, [selfSeat?.seatId]);

  if (!selfSeat || selfSeat.loadout.length === 0) return null;

  // One deploy per round per seat (producer brief 2026-05-13).
  // Once the player has committed an item, every tile in the strip
  // greys out — they're locked in for this round, no second-guessing.
  // pendingDeployments comes back from the SSE delta the moment the
  // server accepts DEPLOY_ITEM, so this flag flips reactively.
  const hasDeployedThisRound = selfSeat.pendingDeployments.length > 0;

  const deployedCount = new Map<ItemId, number>();
  for (const id of selfSeat.pendingDeployments) {
    deployedCount.set(id, (deployedCount.get(id) ?? 0) + 1);
  }
  const ownedCount = new Map<ItemId, number>();
  for (const id of selfSeat.loadout) {
    ownedCount.set(id, (ownedCount.get(id) ?? 0) + 1);
  }
  const tiles = Array.from(ownedCount.entries()).map(([id, owned]) => {
    const deployed = deployedCount.get(id) ?? 0;
    const remaining = owned - deployed;
    return { id, remaining, owned, deployed };
  });

  const fireDeploy = (id: ItemId) => {
    if (!roomId || !playerId) return;
    setFiringItem(id);
    setArmedItem(null);
    void submitWheelCommand(roomId, 'DEPLOY_ITEM', playerId, {
      itemId: id,
    }).catch((e) =>
      console.warn('[ItemDeployStrip] DEPLOY_ITEM failed', e),
    );
    // Safety-net timeout: clear the firing flag after 2s even if the
    // SSE delta never lands (network drop, server reject). The
    // useEffect below clears it earlier when pendingDeployments
    // reflects the deploy. Producer brief 2026-05-15: "Add a
    // spinner/loader on the icon if we think the delay will confuse
    // the user" — the spinner lives on the tile while firingItem is
    // set, so the tile reads as "in flight" through the round-trip.
    window.setTimeout(() => setFiringItem(null), 2000);
  };

  // Clear the firing flag as soon as the SSE delta echoes the
  // deploy back via pendingDeployments — that's the server's
  // confirmation that the item was accepted and the placement is
  // live for every observer. Keeps the spinner up exactly as long
  // as the round-trip takes (rather than a fixed 260ms guess).
  useEffect(() => {
    if (firingItem === null) return;
    if (selfSeat?.pendingDeployments.includes(firingItem)) {
      setFiringItem(null);
    }
  }, [firingItem, selfSeat?.pendingDeployments]);

  const onTileClick = (id: ItemId, usable: boolean) => {
    if (!usable) return;
    if (armedItem === id) {
      // Second tap on the same armed tile → fire. Two-tap commit
      // gives the player a moment to change their mind before
      // burning the item.
      fireDeploy(id);
    } else {
      // First tap or re-arming from another tile → arm this one.
      setArmedItem(id);
    }
  };

  // Backdrop-tap cancel. The strip itself stops propagation; everywhere
  // else (the wheel, the surrounding area) gets a transparent overlay
  // while a tile is armed so a tap anywhere outside the strip clears
  // the armed state.
  const showBackdrop = armedItem !== null;

  return (
    <>
      {showBackdrop ? (
        <div
          aria-hidden
          onClick={() => setArmedItem(null)}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            background: 'rgba(6,13,34,0.35)',
            transition: 'background 120ms ease',
            cursor: 'pointer',
          }}
        />
      ) : null}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          // Producer brief 2026-05-16 — the React-based wheel stage
          // got retired in favour of the Pixi {@code
          // WheelResolveCanvas}, which sizes the wheel from the
          // CANVAS dimensions (not the legacy {@code dims.stageSize}
          // breakpoint table). Anchor the strip to the bottom of
          // the viewport so it stays clear of the wheel rim on
          // every viewport size. The 96px offset clears the bottom
          // seat avatar's orbital position.
          bottom: 96,
          left: 0,
          right: 0,
          zIndex: 6,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 10,
          padding: '0 16px',
        }}
      >
        {tiles.map(({ id, remaining, deployed }) => {
          // A tile is usable when the player hasn't deployed yet
          // this round AND they still own at least one of this item.
          // After the per-round cap fires, every tile reads as spent
          // (greyed-out) except the one they actually deployed —
          // which keeps its commissioned colour as a reminder of
          // what they chose.
          const isDeployed = deployed > 0;
          const usable = remaining > 0 && !hasDeployedThisRound;
          const isArmed = armedItem === id;
          const isFiring = firingItem === id;
          const isOtherArmed = armedItem !== null && !isArmed;
          const iconUrl = ITEM_ICON_URL_BY_ID[id] ?? null;
          return (
            <ItemDeployTile
              key={id}
              id={id}
              iconUrl={iconUrl}
              remaining={remaining}
              deployed={isDeployed}
              usable={usable}
              armed={isArmed}
              firing={isFiring}
              otherArmed={isOtherArmed}
              onTap={() => onTileClick(id, usable)}
            />
          );
        })}
        <style>{`
          @keyframes item-deploy-arm-pulse {
            0%, 100% { opacity: 0; transform: scale(0.95); }
            50%      { opacity: 1; transform: scale(1.05); }
          }
          @keyframes item-deploy-fire {
            0%   { transform: translateY(-8px) scale(1.12); opacity: 1; }
            45%  { transform: translateY(-12px) scale(1.25); opacity: 1; }
            100% { transform: translateY(-12px) scale(0.4); opacity: 0; }
          }
        `}</style>
      </div>
      {armedItem !== null ? (
        <ItemArmedDescription itemId={armedItem} top={120 + stageSize + 32} />
      ) : null}
    </>
  );
}

/** Caption shown below the deploy strip while an item is armed.
 *  Producer brief 2026-05-14: "when selected, show a short
 *  description of what the item does below the item selector." The
 *  copy is the catalog's player-facing description; the strip's
 *  two-tap commit gesture stays unchanged. */
function ItemArmedDescription({
  itemId,
  top,
}: {
  itemId: ItemId;
  /** Top edge of the deploy strip above this panel — used to
   *  position the description immediately below the tile row
   *  regardless of how many rows the strip wraps to. */
  top: number;
}) {
  const meta = ITEMS[itemId];
  return (
    <div
      aria-live="polite"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: top + 80,
        left: 0,
        right: 0,
        zIndex: 6,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        padding: '0 24px',
      }}
    >
      <div
        style={{
          maxWidth: 320,
          textAlign: 'center',
          background: 'rgba(6, 13, 34, 0.78)',
          border: '1px solid rgba(255, 216, 107, 0.45)',
          borderRadius: 12,
          padding: '8px 14px 9px',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.45)',
          fontFamily: '"Fredoka", -apple-system, system-ui',
          color: '#FFE9A6',
          animation: 'item-armed-desc-fade 180ms ease-out',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: '#FFD86B',
            marginBottom: 3,
          }}
        >
          {meta.name}
        </div>
        <div style={{ fontSize: 12, lineHeight: 1.35, color: '#E9F0FF' }}>
          {meta.description}
        </div>
      </div>
      <style>{`
        @keyframes item-armed-desc-fade {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function ItemDeployTile({
  id,
  iconUrl,
  remaining,
  deployed,
  usable,
  armed,
  firing,
  otherArmed,
  onTap,
}: {
  id: ItemId;
  iconUrl: string | null;
  remaining: number;
  /** True when this tile's item is the one the player committed this
   *  round. Gets a distinct "locked-in" treatment (kept colour, no
   *  pulse) so the player remembers what they chose, while the rest
   *  grey out via {@code !usable}. */
  deployed: boolean;
  usable: boolean;
  armed: boolean;
  firing: boolean;
  otherArmed: boolean;
  onTap: () => void;
}) {
  const baseSize = 56;
  return (
    <button
      type="button"
      disabled={!usable}
      onClick={onTap}
      aria-label={`Deploy ${id}`}
      style={{
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: baseSize,
        height: baseSize,
        borderRadius: 14,
        padding: 0,
        background: usable
          ? armed
            ? 'linear-gradient(180deg, #FFE48A 0%, #FFB400 100%)'
            : 'linear-gradient(180deg, #5A3DC4 0%, #2A1E63 100%)'
          : deployed
            ? 'linear-gradient(180deg, #FFD96B 0%, #FFB400 100%)'
            : 'rgba(20,12,40,0.55)',
        border: usable
          ? armed
            ? '2px solid #FFFFFF'
            : '1.5px solid #FFD66B'
          : deployed
            ? '1.5px solid #FFFFFF'
            : '1.5px solid rgba(255,255,255,0.18)',
        cursor: usable ? 'pointer' : 'default',
        boxShadow: usable
          ? armed
            ? '0 6px 14px rgba(255,180,0,0.55), 0 0 0 3px rgba(255,228,138,0.4)'
            : '0 3px 8px rgba(0,0,0,0.45)'
          : 'none',
        opacity: otherArmed && !armed ? 0.4 : 1,
        transform: armed
          ? 'translateY(-8px) scale(1.12)'
          : firing
            ? 'translateY(-4px) scale(1.05)'
            : 'translateY(0) scale(1)',
        transition:
          'transform 160ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 160ms ease, opacity 160ms ease, background 160ms ease',
        // Producer brief 2026-05-15: spinner while we wait for the
        // server SSE delta to echo the deploy. We KEEP the tile
        // visible (no opacity drop, no scale-to-zero) so the
        // spinner can be read against the icon. The tile flips to
        // its "deployed" treatment automatically once
        // pendingDeployments echoes through.
        willChange: 'transform, opacity',
        touchAction: 'manipulation',
      }}
    >
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            width: 44,
            height: 44,
            objectFit: 'contain',
            pointerEvents: 'none',
            filter: armed
              ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.45))'
              : 'drop-shadow(0 2px 3px rgba(0,0,0,0.55))',
          }}
        />
      ) : (
        <span
          style={{
            fontSize: 14,
            fontWeight: 900,
            color: usable ? '#FFD66B' : 'rgba(255,255,255,0.45)',
            fontFamily: '"Fredoka", -apple-system, system-ui',
          }}
        >
          {id.slice(0, 3)}
        </span>
      )}
      {remaining > 1 ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            bottom: -4,
            right: -4,
            minWidth: 18,
            height: 18,
            padding: '0 4px',
            borderRadius: 9,
            background: '#FFD66B',
            color: '#3A2700',
            fontSize: 10,
            fontWeight: 900,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1.5px solid #2A1E63',
            fontFamily: '"Fredoka", -apple-system, system-ui',
            letterSpacing: 0.2,
          }}
        >
          ×{remaining}
        </span>
      ) : null}
      {usable && !armed && !firing ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: -3,
            borderRadius: 17,
            border: '1px solid rgba(255,214,107,0.35)',
            animation: 'item-deploy-arm-pulse 1.6s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />
      ) : null}
      {armed ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: -22,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: 9,
            fontWeight: 900,
            letterSpacing: 1.4,
            color: '#FFE48A',
            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
            fontFamily: '"Fredoka", -apple-system, system-ui',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          READY · TAP TO USE
        </span>
      ) : null}
      {firing ? (
        <>
          {/* Dim overlay + circular spinner — covers the tile while
           *  we wait for the SSE delta to echo the deploy. Producer
           *  brief 2026-05-15: "Add a spinner/loader on the icon if
           *  we think the delay will confuse the user." The overlay
           *  clears the moment pendingDeployments includes this
           *  item (see {@link ItemDeployStrip} useEffect). */}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 14,
              background: 'rgba(10,5,24,0.55)',
              pointerEvents: 'none',
            }}
          />
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 24,
              height: 24,
              marginLeft: -12,
              marginTop: -12,
              borderRadius: '50%',
              border: '3px solid rgba(255,228,138,0.25)',
              borderTopColor: '#FFE48A',
              animation: 'item-deploy-spin 700ms linear infinite',
              pointerEvents: 'none',
            }}
          />
          <style>{`
            @keyframes item-deploy-spin {
              0%   { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </>
      ) : null}
    </button>
  );
}

function WedgeLabel({
  wedge,
  index,
  dims,
  wheelRotationDeg,
}: {
  wedge: WheelWedge;
  index: number;
  dims: WheelDims;
  wheelRotationDeg: number;
}) {
  const midAngleDeg = (index + 0.5) * WEDGE_ARC_DEG;
  const angleRad = ((midAngleDeg - 90) * Math.PI) / 180;
  const cx = dims.wheelSize / 2 + Math.cos(angleRad) * dims.wedgeLabelRadius;
  const cy = dims.wheelSize / 2 + Math.sin(angleRad) * dims.wedgeLabelRadius;
  // Box scales with font size — keeps proportional padding around
  // the glyph.
  const boxW = dims.wedgeLabelFontSize * 2.2;
  const boxH = dims.wedgeLabelFontSize * 1.5;

  return (
    <div
      style={{
        position: 'absolute',
        left: cx - boxW / 2,
        top: cy - boxH / 2,
        width: boxW,
        height: boxH,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Counter-rotate by the current wheel rotation so the glyph
        // stays upright in the viewport even as the wheel spins.
        transform: `rotate(${-wheelRotationDeg}deg)`,
        transformOrigin: 'center',
        pointerEvents: 'none',
        fontSize: dims.wedgeLabelFontSize,
        fontWeight: 900,
        letterSpacing: 0,
        color: '#FFFFFF',
        // Heavy contrast outline so the white text reads against
        // any wedge accent. WebKit ignores text-stroke; the
        // multi-direction text-shadow stack carves the outline.
        textShadow:
          '-1px -1px 0 #1A0E2A, 1px -1px 0 #1A0E2A, -1px 1px 0 #1A0E2A, 1px 1px 0 #1A0E2A, 0 0 4px rgba(0,0,0,0.55)',
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      {wedgeGlyph(wedge)}
    </div>
  );
}

/**
 * BONUS_WEDGE preview marker rendered inside the rotating disc.
 *
 * <p>Sits on top of the wedge at {@code wedgeIndex} so the deployer
 * (and rivals) see the bonus land the moment the SSE delta arrives.
 * Counter-rotates with the wheel so the avatar + ×3 badge stay
 * upright in the viewport regardless of the wheel's current
 * rotation. Producer brief 2026-05-15: "the bonus wedge avatar
 * should stay fixed on the wedge, not rotate with the wheel" —
 * the avatar is centred in the marker (not corner-offset) so it
 * reads as anchored to the wedge throughout the resolve spin.
 *
 * <p>Visual: the deployer's avatar fills the disc; a small gold
 * "×3" coin badge sits in the lower-right corner so the bonus
 * meaning is read at a glance. Self-deployer markers glow cyan;
 * rival deploys glow warm orange so the "your wedge / their wedge"
 * read is immediate.
 */
function BonusWedgeMarker({
  wedgeIndex,
  deployerSeat,
  dims,
  wheelRotationDeg,
  isSelf,
}: {
  wedgeIndex: number;
  deployerSeat: WheelSeatView | null;
  dims: WheelDims;
  wheelRotationDeg: number;
  isSelf: boolean;
}) {
  const midAngleDeg = (wedgeIndex + 0.5) * WEDGE_ARC_DEG;
  const angleRad = ((midAngleDeg - 90) * Math.PI) / 180;
  const cx =
    dims.wheelSize / 2 + Math.cos(angleRad) * dims.wedgeLabelRadius;
  const cy =
    dims.wheelSize / 2 + Math.sin(angleRad) * dims.wedgeLabelRadius;

  const markerSize = Math.round(dims.wedgeLabelFontSize * 2.4);
  const coinBadgeSize = Math.round(markerSize * 0.55);
  const accent = isSelf ? '#6BE0FF' : '#FF8E5A';

  return (
    <div
      style={{
        position: 'absolute',
        left: cx - markerSize / 2,
        top: cy - markerSize / 2,
        width: markerSize,
        height: markerSize,
        // Counter-rotate so the avatar + badge composition stays
        // upright relative to the viewport. No pulse animation —
        // CSS keyframes can't blend with the live rotation value,
        // and the static counter-rotate alone keeps the marker
        // visually fixed on the wedge.
        transform: `rotate(${-wheelRotationDeg}deg)`,
        transformOrigin: 'center',
        pointerEvents: 'none',
      }}
    >
      {/* Deployer avatar fills the marker disc — unmistakable
       *  attribution. Falls back to a neutral coin disc if the
       *  seat lookup fails (defensive — should never happen in
       *  practice since the deployer's seat is in the same
       *  observable payload). */}
      {deployerSeat ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            overflow: 'hidden',
            border: `2px solid ${accent}`,
            boxShadow: `0 0 12px ${accent}, 0 2px 4px rgba(0,0,0,0.55)`,
            background: '#0B0726',
          }}
        >
          <SeatAvatar seat={deployerSeat} size={markerSize} />
        </div>
      ) : (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 35% 30%, #FFE9A6 0%, #FFC940 55%, #B8740A 100%)',
            border: `2px solid ${accent}`,
            boxShadow: `0 0 12px ${accent}, 0 2px 4px rgba(0,0,0,0.55)`,
          }}
        />
      )}
      {/* Small gold coin badge in the lower-right corner with
       *  the "×3" multiplier — tells the player this is a
       *  bonus-wedge, not just a deployer marker. */}
      <div
        style={{
          position: 'absolute',
          right: -coinBadgeSize * 0.2,
          bottom: -coinBadgeSize * 0.2,
          width: coinBadgeSize,
          height: coinBadgeSize,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 35% 30%, #FFE9A6 0%, #FFC940 55%, #B8740A 100%)',
          border: '2px solid #FFFFFF',
          boxShadow: '0 2px 4px rgba(0,0,0,0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '"Fredoka", -apple-system, system-ui',
          fontWeight: 900,
          fontSize: Math.round(coinBadgeSize * 0.45),
          color: '#3A1E04',
          textShadow: '0 1px 0 rgba(255,255,255,0.55)',
        }}
      >
        ×3
      </div>
    </div>
  );
}

/**
 * Empty seat slot — visible during STAKE when no seat has claimed
 * this position yet. Tappable for the local player so they can
 * SELECT_SEAT into it. After the STAKE → WHEEL_RESOLVE transition
 * fills any leftover slots, no EmptySlotMarker should render on
 * screen — the wheel runs with a full ring of avatars.
 */
function EmptySlotMarker({
  slot,
  cx,
  cy,
  avatarDiameter,
  onTap,
}: {
  slot: number;
  cx: number;
  cy: number;
  avatarDiameter: number;
  onTap: (() => void) | null;
}) {
  const tappable = !!onTap;
  const hitPad = tappable ? Math.round(avatarDiameter * 0.18) : 0;
  // Producer brief 2026-05-12: the wheel was "too busy" — the per-
  // slot "TAP TO SIT" callouts duplicated the dashed-circle
  // affordance and clashed with the seat avatars / wedge labels.
  // The pulsing dashed ring (when tappable) carries the affordance
  // on its own; the "Tap a seat · Use items" hint below the wheel
  // names the gesture once.
  return (
    <button
      type="button"
      disabled={!tappable}
      style={{
        position: 'absolute',
        left: cx - avatarDiameter / 2 - hitPad,
        top: cy - avatarDiameter / 2 - hitPad,
        width: avatarDiameter + hitPad * 2,
        height: avatarDiameter + hitPad * 2,
        zIndex: 4,
        pointerEvents: tappable ? 'auto' : 'none',
        cursor: tappable ? 'pointer' : 'default',
        background: 'transparent',
        border: 'none',
        padding: 0,
        font: 'inherit',
        color: 'inherit',
        touchAction: 'manipulation',
      }}
      onClick={onTap ?? undefined}
      aria-label={tappable ? `Sit in slot ${slot}` : `Empty slot ${slot}`}
    >
      <div
        style={{
          position: 'absolute',
          left: hitPad,
          top: hitPad,
          width: avatarDiameter,
          height: avatarDiameter,
          borderRadius: '50%',
          border: '3px dashed rgba(255,228,138,0.85)',
          background: 'rgba(20,12,40,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,228,138,0.85)',
          fontSize: Math.round(avatarDiameter * 0.42),
          fontWeight: 900,
          fontFamily: '"Fredoka", system-ui',
          textShadow: '0 1px 2px rgba(0,0,0,0.65)',
          animation: tappable
            ? 'wheel-empty-slot-pulse 1500ms ease-in-out infinite'
            : 'none',
          pointerEvents: 'none',
        }}
      >
        ＋
      </div>
      {tappable ? (
        <style>{`
          @keyframes wheel-empty-slot-pulse {
            0%, 100% { opacity: 0.55; transform: scale(0.95); }
            50%      { opacity: 1;    transform: scale(1.05); }
          }
        `}</style>
      ) : null}
    </button>
  );
}

function SeatPointer({
  seat,
  cx,
  cy,
  pointerAngleDeg,
  outcome,
  actionCards,
  isSelf,
  revealed,
  landed,
  avatarDiameter,
  chipMinWidth,
  chipFontSize,
  interactive,
  onTap,
}: {
  seat: WheelSeatView;
  cx: number;
  cy: number;
  pointerAngleDeg: number;
  outcome: WheelDialOutcome | null;
  /** All action cards for this resolve — OutcomeChip filters by
   *  seatId to render the seat's per-item delta breakdown. */
  actionCards: ReadonlyArray<WheelActionCard>;
  isSelf: boolean;
  revealed: boolean;
  landed: boolean;
  avatarDiameter: number;
  chipMinWidth: number;
  chipFontSize: number;
  /** STAKE phase — render the avatar as a tappable button so the
   *  player can SELECT_SEAT into this slot. */
  interactive: boolean;
  /** Fires on tap; null disables the click (own slot, or non-pre-spin). */
  onTap: (() => void) | null;
}) {
  // Position the chip outward of the avatar along the radial
  // direction. 0.85× sits the chip's anchor just past the avatar's
  // outer edge — chip body extends slightly into the page margin
  // (mobile) or past the rim (desktop), both legible.
  const angleRad = ((pointerAngleDeg - 90) * Math.PI) / 180;
  const chipOffsetX = Math.cos(angleRad) * (avatarDiameter * 0.85);
  const chipOffsetY = Math.sin(angleRad) * (avatarDiameter * 0.85);

  const tappable = interactive && !!onTap;
  // Expand the hit target a bit beyond the avatar disc so tap is
  // forgiving on small mobile viewports.
  const hitPad = tappable ? Math.round(avatarDiameter * 0.18) : 0;
  // Producer brief 2026-05-12: the wheel was "too busy" — the
  // per-seat SWAP / YOU text callouts duplicated affordances the
  // dashed pulse + isSelf scale-up already convey, and clashed
  // with the wedge labels. Single hint below the wheel
  // ("Tap a seat · Use items") now names the gesture once.
  return (
    <button
      type="button"
      disabled={!tappable}
      style={{
        position: 'absolute',
        left: cx - avatarDiameter / 2 - hitPad,
        top: cy - avatarDiameter / 2 - hitPad,
        width: avatarDiameter + hitPad * 2,
        height: avatarDiameter + hitPad * 2,
        zIndex: 4,
        // During STAKE the whole pointer is tappable. Outside STAKE
        // we want the avatar disc itself to ignore pointer events so
        // wedge-area taps still flow through.
        pointerEvents: interactive ? 'auto' : 'none',
        cursor: onTap ? 'pointer' : 'default',
        transition: 'transform 200ms ease-out',
        transform: interactive && isSelf ? 'scale(1.06)' : 'scale(1)',
        background: 'transparent',
        border: 'none',
        padding: 0,
        font: 'inherit',
        color: 'inherit',
        touchAction: 'manipulation',
      }}
      onClick={onTap ?? undefined}
      aria-label={
        interactive
          ? isSelf
            ? `Your seat`
            : `Swap into ${seat.displayName}'s slot`
          : seat.displayName
      }
    >
      {/* Tappable dashed pulse around the avatar — silent affordance
       *  on bot avatars during STAKE so the player reads "swap-able"
       *  without a text overlay. */}
      {tappable ? (
        <>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: hitPad,
              left: hitPad,
              width: avatarDiameter,
              height: avatarDiameter,
              borderRadius: '50%',
              border: '2px dashed rgba(255,228,138,0.85)',
              animation: 'wheel-tap-pulse 1500ms ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
          <style>{`
            @keyframes wheel-tap-pulse {
              0%, 100% { opacity: 0.45; transform: scale(0.95); }
              50%      { opacity: 1;   transform: scale(1.05); }
            }
          `}</style>
        </>
      ) : null}
      {/* Self-seat affordance is the 1.06 scale-up on the parent
       *  button (interactive && isSelf), plus the gold ring boxShadow
       *  on the avatar disc — no text "YOU" badge needed once the
       *  wheel was decluttered. */}
      {/* Avatar disc with the seat's portrait. SHIELD halo / HIT-
       *  impact tints layer on top of the existing self-halo when
       *  the appropriate items fire on this seat's outcome. The
       *  avatar disc is decorative — taps fall through to the parent
       *  button so iOS Safari can't consume the touch on the styled
       *  child before the click fires. */}
      <div
        style={{
          position: 'absolute',
          top: hitPad,
          left: hitPad,
          width: avatarDiameter,
          height: avatarDiameter,
          borderRadius: '50%',
          overflow: 'hidden',
          // Producer brief 2026-05-16 — the "+1 PEG · BOOST"
          // telegraph badge sits radially outward from this disc;
          // bump the avatar's stacking order so the badge tucks
          // BEHIND the portrait when the two overlap (the badge is
          // sibling-rendered later in JSX, so without an explicit
          // z-index it would paint on top of the avatar).
          zIndex: 2,
          boxShadow: isSelf
            ? '0 0 0 2px #FFC940, 0 0 12px rgba(255,201,64,0.5), 0 4px 8px rgba(0,0,0,0.4)'
            : '0 0 0 2px #060d22, 0 4px 8px rgba(0,0,0,0.4)',
          background: '#0B1838',
          pointerEvents: 'none',
        }}
      >
        <SeatAvatar seat={seat} size={avatarDiameter} />
        {/* SHIELD halo — pulses cyan when an incoming HIT was blocked
         *  this round. Fades in with the chip reveal. */}
        {revealed && outcome?.appliedItems.includes('SHIELD') ? (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: '3px solid #5DD5FF',
              boxShadow:
                'inset 0 0 12px rgba(93,213,255,0.55), 0 0 18px rgba(93,213,255,0.55)',
              animation: 'wheel-shield-pulse 900ms ease-out',
            }}
          />
        ) : null}
        {/* HIT-impact flash — red overlay when the deployer fired a
         *  hit, or when this seat got hit (wedge shifted). */}
        {revealed && outcome?.appliedItems.includes('HIT') ? (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(255,107,107,0.55) 0%, rgba(255,107,107,0) 70%)',
              animation: 'wheel-hit-flash 700ms ease-out',
            }}
          />
        ) : null}
      </div>
      <style>{`
        @keyframes wheel-shield-pulse {
          0% { transform: scale(0.75); opacity: 0; }
          40% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 0.85; }
        }
        @keyframes wheel-hit-flash {
          0% { opacity: 0; transform: scale(0.6); }
          40% { opacity: 1; transform: scale(1.1); }
          100% { opacity: 0; transform: scale(1.25); }
        }
      `}</style>
      {/* Inward-pointing triangle indicator — tells the player which
       *  side of the avatar reads the wedge. Rotated to point toward
       *  the wheel centre. Anchored to the avatar disc's centre
       *  (NOT the button's centre — the callout area extends the
       *  button below the avatar, so % anchors would drift). */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: hitPad + avatarDiameter / 2,
          left: hitPad + avatarDiameter / 2,
          width: 0,
          height: 0,
          borderLeft: `${avatarDiameter * 0.14}px solid transparent`,
          borderRight: `${avatarDiameter * 0.14}px solid transparent`,
          borderBottom: `${avatarDiameter * 0.24}px solid #FFC940`,
          transform: `translate(-50%, -50%) rotate(${pointerAngleDeg + 180}deg) translateY(-${avatarDiameter / 2 + 4}px)`,
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.55))',
          pointerEvents: 'none',
        }}
      />
      {/* STAKE-phase BOOST telegraph — a small +1 badge floats next
       *  to the self-seat's pointer when BOOST is in the seat's
       *  pendingDeployments. Tells the player "this BOOST is locked
       *  in; the wheel will read +1 peg toward a better wedge."
       *  Only shown for the local player (other seats' deploys read
       *  via the post-spin "+1 PEG · BOOST" callout in the outcome
       *  chip). Counts boosts so a multi-deploy reads as +N. */}
      {(() => {
        if (!isSelf || !interactive) return null;
        const boostCount = seat.pendingDeployments.filter(
          (id) => id === 'BOOST',
        ).length;
        const tripleBoostCount = seat.pendingDeployments.filter(
          (id) => id === 'TRIPLE_BOOST',
        ).length;
        const totalPegs = boostCount + tripleBoostCount * 3;
        if (totalPegs === 0) return null;
        const label = tripleBoostCount > 0
          ? `+${totalPegs} PEG · 3× BOOST`
          : totalPegs > 1
            ? `+${totalPegs} PEG · BOOST`
            : '+1 PEG · BOOST';
        // Producer brief 2026-05-16: the previous nested rotate
        // transform was placing the badge radially INWARD (toward
        // the wheel rim) for left/right seats, so it overlapped
        // the gold ring. Compute the offset directly with cos/sin
        // so the badge sits cleanly OUTSIDE the avatar regardless
        // of which seat slot the player occupies. 0° pointer angle
        // = TOP of wheel, so subtract 90° to map onto standard
        // polar where 0° = right (the math for {@code cos,sin}).
        const radialAngleRad =
          ((pointerAngleDeg - 90) * Math.PI) / 180;
        const radialDist = avatarDiameter * 0.95;
        const dx = Math.cos(radialAngleRad) * radialDist;
        const dy = Math.sin(radialAngleRad) * radialDist;
        return (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: hitPad + avatarDiameter / 2 + dy,
              left: hitPad + avatarDiameter / 2 + dx,
              transform: 'translate(-50%, -50%)',
              padding: '2px 6px',
              borderRadius: 8,
              background: 'linear-gradient(180deg, #FFE48A 0%, #FFB400 100%)',
              border: '1.5px solid #FFFFFF',
              boxShadow:
                '0 2px 6px rgba(0,0,0,0.55), 0 0 8px rgba(255,228,138,0.65)',
              color: '#3A2700',
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: 0.4,
              fontFamily: '"Fredoka", -apple-system, system-ui',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              animation:
                'wheel-boost-telegraph 1500ms ease-in-out infinite',
            }}
          >
            {label}
          </div>
        );
      })()}
      <style>{`
        @keyframes wheel-boost-telegraph {
          0%, 100% { filter: brightness(1); }
          50%      { filter: brightness(1.15); }
        }
      `}</style>
      {/* Outcome chip — fans out outward from the pointer once the
       *  wheel lands. Shows the wedge they read + per-seat payout. */}
      {outcome ? (
        <OutcomeChip
          seat={seat}
          outcome={outcome}
          actionCards={actionCards}
          isSelf={isSelf}
          offsetX={chipOffsetX}
          offsetY={chipOffsetY}
          revealed={revealed}
          landed={landed}
          anchorDiameter={avatarDiameter}
          minWidth={chipMinWidth}
          baseFontSize={chipFontSize}
        />
      ) : null}
    </button>
  );
}

/** Line item rendered on the OutcomeChip — one row per action card
 *  involving this seat. The label is a short noun phrase; the delta
 *  is signed; the colour follows item-family conventions (gold for
 *  self-gain, red for sabotage received, purple for payout-mods,
 *  green for steals). */
interface OutcomeLineItem {
  label: string;
  delta: number;
  color: string;
}

/** Filter the resolve's action cards for ones that touch
 *  {@code seatId}, build a label + delta + colour for each. Hides
 *  cards with zero delta (BLOCKED hits, mass-summary cards where
 *  the per-rival delta lives on a sibling card). Hides X2_BONUS
 *  and COIN_STASH for non-self viewers — those callouts stay
 *  PRIVATE to the deployer. */
function buildOutcomeLineItems(
  cards: ReadonlyArray<WheelActionCard>,
  seatId: number,
  isSelf: boolean,
): OutcomeLineItem[] {
  const items: OutcomeLineItem[] = [];
  for (const card of cards) {
    const sourceMatch = card.sourceSeatId === seatId;
    const targetMatch = card.targetSeatId === seatId;
    if (!sourceMatch && !targetMatch) continue;
    // Self-buff side of an item the seat deployed.
    if (sourceMatch && card.sourceDelta !== 0) {
      // X2_BONUS + COIN_STASH stay PRIVATE — other players don't
      // see them; the bigger payout number tells the story.
      if (
        !isSelf &&
        (card.itemId === 'X2_BONUS' || card.itemId === 'COIN_STASH')
      ) {
        continue;
      }
      items.push({
        label: itemLineLabel(card.itemId, 'source'),
        delta: card.sourceDelta,
        color: itemLineColor(card.itemId, card.sourceDelta),
      });
    }
    // Target side of an incoming item (sabotage hit, lightning,
    // steal-from). Even blocked HITs contribute a 0-delta — skip
    // those; the action-card overlay already showed the "BLOCKED"
    // beat.
    if (targetMatch && card.targetDelta !== 0) {
      items.push({
        label: itemLineLabel(card.itemId, 'target'),
        delta: card.targetDelta,
        color: itemLineColor(card.itemId, card.targetDelta),
      });
    }
  }
  return items;
}

function itemLineLabel(itemId: ItemId, side: 'source' | 'target'): string {
  switch (itemId) {
    case 'BOOST':
      return 'Boost';
    case 'TRIPLE_BOOST':
      return '3× Boost';
    case 'HIT':
      return side === 'source' ? 'Hit sent' : 'Hit';
    case 'BOMB':
      return side === 'source' ? 'Bomb sent' : 'Bomb';
    case 'POW':
      return side === 'source' ? 'POW!' : 'POW';
    case 'SHIELD':
      return 'Shield';
    case 'LIGHTNING':
      return side === 'source' ? 'Lightning' : 'Lightning';
    case 'SHAKEDOWN':
      return side === 'source' ? 'Stole' : 'Robbed';
    case 'X2_BONUS':
      return '×2 winnings';
    case 'COIN_STASH':
      return 'Coin stash';
    default:
      return itemId;
  }
}

function itemLineColor(itemId: ItemId, delta: number): string {
  if (delta < 0) return '#FF8E9D'; // red — loss
  switch (itemId) {
    case 'BOOST':
    case 'TRIPLE_BOOST':
    case 'COIN_STASH':
      return '#FFD86B'; // gold — self-gain
    case 'X2_BONUS':
      return '#B475FF'; // purple — multiplier
    case 'SHAKEDOWN':
      return '#9DE54E'; // green — steal-gain
    default:
      return '#FFE4A0';
  }
}

function OutcomeChip({
  seat,
  outcome,
  actionCards,
  isSelf,
  offsetX,
  offsetY,
  revealed,
  landed,
  anchorDiameter,
  minWidth,
  baseFontSize,
}: {
  seat: WheelSeatView;
  outcome: WheelDialOutcome;
  /** Per-resolve action cards. We filter for cards where this seat
   *  is the source OR the target and render an itemised breakdown
   *  ("BOOST +60", "LIGHTNING −80") so the player can read exactly
   *  where the coin delta came from. Producer brief 2026-05-14:
   *  "we need better visibility that coins were won/lost because
   *   of an action." */
  actionCards: ReadonlyArray<WheelActionCard>;
  /** True when this chip belongs to the local player's seat. Used
   *  to gate PRIVATE callouts (X2_BONUS, COIN_STASH) that the
   *  deployer sees but other players don't. */
  isSelf: boolean;
  offsetX: number;
  offsetY: number;
  revealed: boolean;
  landed: boolean;
  anchorDiameter: number;
  minWidth: number;
  baseFontSize: number;
}) {
  const name = useSeatName(seat);
  const wedge = outcome.wedge;
  const accent = wedgeAccent(wedge);
  const isCoins = wedge.kind === 'COINS';
  const isBust = wedge.kind === 'BANKRUPT' || (isCoins && (wedge.multiplier ?? 0) === 0);
  const grantedItem = outcome.grantedItem ?? null;

  // Filter action cards relevant to this seat — show the source
  // delta when this seat deployed (self-buff or steal-gain), or
  // the target delta when this seat was hit/halved/stolen-from.
  // Cards with zero delta (BLOCKED hits, mass-summary lightning
  // cards) are dropped from the itemised list — they're already
  // visualised by the action-card overlay sequence.
  const lineItems = buildOutcomeLineItems(actionCards, seat.seatId, isSelf);

  return (
    <div
      style={{
        position: 'absolute',
        top: anchorDiameter / 2 + offsetY,
        left: anchorDiameter / 2 + offsetX,
        transform: `translate(-50%, -50%) scale(${revealed ? 1 : 0.6})`,
        opacity: landed && revealed ? 1 : 0,
        transition:
          'transform 280ms cubic-bezier(0.2, 0.7, 0.4, 1.4), opacity 240ms ease-out',
        padding: '4px 8px',
        borderRadius: 8,
        background: 'rgba(10, 5, 24, 0.92)',
        border: `1px solid ${accent}aa`,
        boxShadow: '0 4px 10px rgba(0,0,0,0.45)',
        minWidth,
        textAlign: 'center',
        pointerEvents: 'none',
        fontFamily: '"Fredoka", -apple-system, system-ui',
        whiteSpace: 'nowrap',
      }}
    >
      {lineItems.length > 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            marginBottom: 2,
          }}
        >
          {lineItems.map((li, idx) => (
            <div
              key={idx}
              style={{
                fontSize: baseFontSize - 2,
                fontWeight: 900,
                color: li.color,
                letterSpacing: 0.5,
                textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 6,
              }}
            >
              <span>{li.label}</span>
              <span>
                {li.delta > 0 ? '+' : ''}
                {li.delta}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {grantedItem ? (
        <div
          style={{
            fontSize: baseFontSize - 2,
            fontWeight: 900,
            color: '#9DE54E',
            letterSpacing: 1,
            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          }}
        >
          ＋{grantedItem.replace(/_/g, ' ')}
        </div>
      ) : null}
      <div
        style={{
          fontSize: baseFontSize - 2,
          fontWeight: 700,
          color: 'rgba(255,255,255,0.55)',
          letterSpacing: 1,
        }}
      >
        {name.split(' ')[0]}
      </div>
      <div
        style={{
          fontSize: baseFontSize,
          fontWeight: 900,
          color: accent,
          letterSpacing: 0.5,
          marginTop: 1,
        }}
      >
        {wedgeLongLabel(wedge)}
      </div>
      {isCoins ? (
        <div
          style={{
            marginTop: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            fontSize: baseFontSize + 1,
            fontWeight: 800,
            color: outcome.payout > 0 ? '#FFD700' : 'rgba(255,255,255,0.45)',
          }}
        >
          <Icon name="coin" size={baseFontSize - 1} alt="" />
          <span>{formatCompact(outcome.payout)}</span>
        </div>
      ) : (
        <div
          style={{
            marginTop: 1,
            fontSize: baseFontSize - 1,
            fontWeight: 700,
            color: isBust ? '#FF8E9D' : 'rgba(255,255,255,0.7)',
          }}
        >
          {wedge.kind === 'BANKRUPT' ? 'BUST!' :
            wedge.kind === 'SKIP_NEXT' ? 'SKIP NEXT' :
            wedge.kind === 'EXTRA_ITEM' ? '+1 ITEM' :
            wedge.kind === 'SPECIAL_PRIZE' ? '×2 NEXT' :
            ''}
        </div>
      )}
    </div>
  );
}

// ---- wedge styling helpers ------------------------------------------------

/** Per-wedge accent — mirrors the round-variant tuning. COINS climbs
 *  through cool → warm shades with multiplier; effect wedges have
 *  their own distinct hues. Easy to swap once Pencil delivers the
 *  final palette. */
export function wedgeAccent(wedge: WheelWedge): string {
  switch (wedge.kind) {
    case 'COINS': {
      const m = wedge.multiplier ?? 0;
      if (m === 0) return '#5C6BC0';
      if (m <= 2) return '#9DE54E';
      if (m <= 5) return '#1FC8C8';
      if (m <= 10) return '#9B7BFF';
      if (m <= 25) return '#FF6B9D';
      return '#FFC940';
    }
    case 'BANKRUPT':       return '#E8344E';
    case 'SKIP_NEXT':      return '#7A5BFF';
    case 'EXTRA_ITEM':     return '#6BCBFF';
    case 'SPECIAL_PRIZE':  return '#FFC940';
  }
}

/** Short glyph for in-wedge labels. ~3 chars max so it fits at the
 *  15°-arc width. */
export function wedgeGlyph(wedge: WheelWedge): string {
  switch (wedge.kind) {
    case 'COINS':         return `×${wedge.multiplier ?? 0}`;
    case 'BANKRUPT':      return '✕';
    case 'SKIP_NEXT':     return '⏭';
    case 'EXTRA_ITEM':    return '+1';
    case 'SPECIAL_PRIZE': return '★';
  }
}

/** Longer human-readable label for outcome chips + the reveal board's
 *  composition pills. */
export function wedgeLongLabel(wedge: WheelWedge): string {
  switch (wedge.kind) {
    case 'COINS':         return `×${wedge.multiplier ?? 0}`;
    case 'BANKRUPT':      return 'BANKRUPT';
    case 'SKIP_NEXT':     return 'SKIP TURN';
    case 'EXTRA_ITEM':    return 'EXTRA ITEM';
    case 'SPECIAL_PRIZE': return 'BIG PRIZE';
  }
}

// ---- rim lights + confetti (preserved from prior WheelResolveView) -------

function RimLights({
  diameter,
  centerX,
  centerY,
  count,
  cycleMs,
}: {
  diameter: number;
  centerX: number;
  centerY: number;
  count: number;
  cycleMs: number;
}) {
  const radius = diameter / 2;
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: centerX - radius,
        top: centerY - radius,
        width: diameter,
        height: diameter,
        pointerEvents: 'none',
        zIndex: 3,
      }}
    >
      <style>{`
        @keyframes wheel-rim-light-pulse {
          0%, 80%, 100% { opacity: 0.25; transform: translate(-50%, -50%) scale(0.7); }
          15%           { opacity: 1;    transform: translate(-50%, -50%) scale(1.05); }
        }
      `}</style>
      {Array.from({ length: count }, (_, i) => {
        const angleRad = ((i / count) * 360 - 90) * (Math.PI / 180);
        const x = radius + Math.cos(angleRad) * radius;
        const y = radius + Math.sin(angleRad) * radius;
        const delaySec = -(i / count) * (cycleMs / 1000);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background:
                'radial-gradient(circle at 35% 30%, #FFFCE0, #FFD66B 55%, #B07F00)',
              boxShadow: '0 0 6px rgba(255,228,138,0.85), 0 0 12px rgba(255,201,64,0.6)',
              transform: 'translate(-50%, -50%) scale(0.7)',
              animation: `wheel-rim-light-pulse ${cycleMs}ms linear infinite`,
              animationDelay: `${delaySec}s`,
            }}
          />
        );
      })}
    </div>
  );
}

function ConfettiBurst({
  centerX,
  centerY,
}: {
  centerX: number;
  centerY: number;
}) {
  const particles = useMemo(() => {
    const COUNT = 50;
    const COLORS = ['#FFD66B', '#FF6BB5', '#6BCBFF', '#9DE54E', '#B984FF'];
    return Array.from({ length: COUNT }, (_, i) => {
      const angle = (Math.random() * 360) * (Math.PI / 180);
      const upBias = -50 - Math.random() * 90;
      const sideBias = (Math.random() - 0.5) * 240;
      const dx = Math.cos(angle) * 60 + sideBias;
      const dy = Math.sin(angle) * 60 + upBias;
      const finalDx = dx + (Math.random() - 0.5) * 80;
      const finalDy = dy + 360 + Math.random() * 120;
      const rot = (Math.random() - 0.5) * 1080;
      const size = 5 + Math.random() * 6;
      return {
        i,
        color: COLORS[i % COLORS.length] ?? '#FFD66B',
        size,
        ratio: 0.4 + Math.random() * 0.5,
        peakDx: dx,
        peakDy: dy,
        finalDx,
        finalDy,
        rot,
        durationMs: 1800 + Math.random() * 600,
        delayMs: Math.random() * 200,
      };
    });
  }, []);

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: centerX,
        top: centerY,
        width: 0,
        height: 0,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {particles.map((p) => (
        <div
          key={p.i}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size * p.ratio,
            left: 0,
            top: 0,
            background: p.color,
            borderRadius: 1,
            opacity: 0,
            transform: 'translate(-50%, -50%)',
            animation: `wheel-confetti-${p.i} ${p.durationMs}ms cubic-bezier(0.2, 0.6, 0.4, 1) ${p.delayMs}ms forwards`,
          }}
        />
      ))}
      <style>{particles
        .map(
          (p) => `
        @keyframes wheel-confetti-${p.i} {
          0%   { opacity: 0; transform: translate(-50%, -50%) rotate(0deg); }
          12%  { opacity: 1; }
          50%  { transform: translate(calc(-50% + ${p.peakDx}px), calc(-50% + ${p.peakDy}px)) rotate(${p.rot * 0.5}deg); }
          85%  { opacity: 1; }
          100% { opacity: 0; transform: translate(calc(-50% + ${p.finalDx}px), calc(-50% + ${p.finalDy}px)) rotate(${p.rot}deg); }
        }
      `,
        )
        .join('')}</style>
    </div>
  );
}

// ---- helpers --------------------------------------------------------------

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
